import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Search,
  Trash2,
  Upload,
  FolderOpen,
  FolderPlus,
  FileText,
  Video,
  Mic,
  Terminal,
  Share2,
  RefreshCw,
  Clock,
  HardDrive,
  LayoutGrid,
  List,
  MoreVertical,
  Edit2,
  Move,
  Download,
  ChevronRight,
  Home,
  CloudOff,
  Cloud,
  X,
  Check,
  File,
  BarChart3,
  Settings,
  RotateCcw,
  AlertTriangle,
  Folder,
  TrendingUp,
  Database,
  ArrowUpRight,
  Sparkles,
  Users,
  ExternalLink,
  Import,
  ChevronDown,
  Plus,
  BookOpen,
} from 'lucide-react';
import { Card, CardContent } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Progress } from '@easyflow/ui-frontend/components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@easyflow/ui-frontend/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@easyflow/ui-frontend/components/ui/dialog';
import { Checkbox } from '@easyflow/ui-frontend/components/ui/checkbox';
import { storageFileUrl } from '@/lib/storage-url';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';
import type {
  FileManagerFile,
  FileManagerFolder,
  FileManagerFolderTreeNode,
  FileManagerQuota,
  FileManagerStorageSettings,
  FileManagerAnalytics,
  FileManagerFilesResponse,
  FileManagerFoldersResponse,
  FileManagerFolderTreeResponse,
  FileManagerSharedResponse,
  FileManagerQuotaResponse,
  FileManagerSettingsResponse,
  FileManagerAnalyticsResponse,
  FileManagerDownloadResponse,
  FileManagerAiSummaryResponse,
  FileManagerOkResponse,
  GoogleDriveFile,
  GoogleDriveAccount,
  GoogleDriveAccountsResponse,
  GoogleDriveFilesResponse,
  GoogleDriveFoldersResponse,
  GoogleOauthStartData,
  WikiFileImpact,
  WorkgroupFile,
  WorkgroupSummary,
  TenantUsersListResponse,
} from '@easyflow/shared';

// Shape di risposta condivise con il backend (`@easyflow/shared`): alias
// locali per leggibilità, nessun tipo di risposta ridichiarato qui.
type FileItem = FileManagerFile;
type Workgroup = WorkgroupSummary;
type StorageSettings = FileManagerStorageSettings;
type GroupFile = WorkgroupFile;
type FolderItem = FileManagerFolder;
type FolderTreeNode = FileManagerFolderTreeNode;
type QuotaInfo = FileManagerQuota;

interface ShareUser {
  id: string;
  name: string;
  email: string;
}

type AnalyticsData = FileManagerAnalytics;
type DriveFile = GoogleDriveFile;
type GoogleAccount = GoogleDriveAccount;

const CATEGORIES = [
  { id: 'document', label: 'Documenti', icon: FileText, color: 'text-blue-500', bgColor: 'bg-blue-50 dark:bg-blue-900/30', adminOnly: false },
  { id: 'video', label: 'Video', icon: Video, color: 'text-purple-500', bgColor: 'bg-purple-50 dark:bg-purple-900/30', adminOnly: false },
  { id: 'recording', label: 'Registrazioni', icon: Mic, color: 'text-green-500', bgColor: 'bg-green-50 dark:bg-green-900/30', adminOnly: true },
  // Task #907: Company Wiki Files. Always visible to admins (the system folder
  // is provisioned for every tenant), independent from the company_wiki module.
  { id: 'wiki', label: 'Company Wiki Files', icon: BookOpen, color: 'text-amber-500', bgColor: 'bg-amber-50 dark:bg-amber-900/30', adminOnly: true },
];

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getFileIcon(mimeType: string) {
  if (mimeType?.startsWith('video/')) return <Video className="h-5 w-5 text-purple-500" />;
  if (mimeType?.startsWith('audio/')) return <Mic className="h-5 w-5 text-green-500" />;
  if (mimeType?.startsWith('image/')) return <File className="h-5 w-5 text-pink-500" />;
  if (mimeType?.includes('pdf')) return <FileText className="h-5 w-5 text-red-500" />;
  if (mimeType?.includes('text') || mimeType?.includes('log')) return <Terminal className="h-5 w-5 text-orange-500" />;
  return <FileText className="h-5 w-5 text-blue-500" />;
}

function getCategoryLabel(catId: string): string {
  return CATEGORIES.find(c => c.id === catId)?.label || catId;
}

export default function FileManager() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [files, setFiles] = useState<FileItem[]>([]);
  const [folders, setFolders] = useState<FolderItem[]>([]);
  const [folderTree, setFolderTree] = useState<FolderTreeNode[]>([]);
  const [quota, setQuota] = useState<QuotaInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [currentCategory, setCurrentCategory] = useState<string | null>(null);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<{ id: string | null; name: string }[]>([]);
  const [sidebarSection, setSidebarSection] = useState<'categories' | 'shared' | 'trash' | 'analytics' | 'settings' | 'drive' | 'groups'>('categories');
  const [groupsList, setGroupsList] = useState<Workgroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<Workgroup | null>(null);
  const [groupFiles, setGroupFiles] = useState<GroupFile[]>([]);

  const [newFolderDialog, setNewFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameDialog, setRenameDialog] = useState<{ open: boolean; file: FileItem | null }>({ open: false, file: null });
  const [newName, setNewName] = useState('');
  // Task #2834 — stati dedicati per le azioni sui file di gruppo (tabella separata).
  const [groupRenameDialog, setGroupRenameDialog] = useState<{ open: boolean; file: GroupFile | null }>({ open: false, file: null });
  const [groupNewName, setGroupNewName] = useState('');
  const [groupAiSummaryDialog, setGroupAiSummaryDialog] = useState<{ open: boolean; file: GroupFile | null; loading: boolean; summary: string | null; generatedAt: string | null }>({ open: false, file: null, loading: false, summary: null, generatedAt: null });
  const [moveDialog, setMoveDialog] = useState<{ open: boolean; file: FileItem | null }>({ open: false, file: null });
  const [shareDialog, setShareDialog] = useState<{ open: boolean; file: FileItem | null; folder: FolderItem | null }>({ open: false, file: null, folder: null });
  const [shareGroups, setShareGroups] = useState<Workgroup[]>([]);
  const [selectedShareGroups, setSelectedShareGroups] = useState<string[]>([]);
  const [shareUsers, setShareUsers] = useState<ShareUser[]>([]);
  const [selectedShareUsers, setSelectedShareUsers] = useState<string[]>([]);
  const [sharePermission, setSharePermission] = useState<'read' | 'readwrite'>('read');
  const [shareExpiry, setShareExpiry] = useState('');
  const [sharedFiles, setSharedFiles] = useState<FileItem[]>([]);
  const [trashFiles, setTrashFiles] = useState<FileItem[]>([]);

  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [storageSettings, setStorageSettings] = useState<StorageSettings | null>(null);
  const [settingsSaving, setSettingsSaving] = useState(false);

  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounter = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [driveFiles, setDriveFiles] = useState<DriveFile[]>([]);
  const [driveAccounts, setDriveAccounts] = useState<GoogleAccount[]>([]);
  const [selectedDriveAccount, setSelectedDriveAccount] = useState<string>('');
  const [driveFolderId, setDriveFolderId] = useState<string>('root');
  const [driveBreadcrumbs, setDriveBreadcrumbs] = useState<{ id: string; name: string }[]>([]);
  const [driveLoading, setDriveLoading] = useState(false);
  const [driveSearchTerm, setDriveSearchTerm] = useState('');
  const [syncToDriveDialog, setSyncToDriveDialog] = useState<{ open: boolean; file: FileItem | null }>({ open: false, file: null });
  const [syncDriveFolders, setSyncDriveFolders] = useState<DriveFile[]>([]);
  const [syncDriveFolderId, setSyncDriveFolderId] = useState<string>('');
  const [syncDriveLoading, setSyncDriveLoading] = useState(false);
  const [importFromDriveDialog, setImportFromDriveDialog] = useState<{ open: boolean; driveFile: DriveFile | null }>({ open: false, driveFile: null });
  const [importCategory, setImportCategory] = useState<string>('document');
  const [importFolderId, setImportFolderId] = useState<string>('');
  const [importLoading, setImportLoading] = useState(false);

  const [aiSummaryDialog, setAiSummaryDialog] = useState<{ open: boolean; file: FileItem | null; loading: boolean; summary: string | null; generatedAt: string | null }>({ open: false, file: null, loading: false, summary: null, generatedAt: null });

  // Task #1326 — dialog di conferma per delete permanente quando il file è
  // referenziato da almeno 1 fonte `company_wiki_sources`.
  const [wikiDeleteDialog, setWikiDeleteDialog] = useState<{
    open: boolean;
    file: FileItem | null;
    impact: { count: number; sources: Array<{ id: string; title: string; kind: string }> };
    cascade: boolean;
    deleting: boolean;
  }>({ open: false, file: null, impact: { count: 0, sources: [] }, cascade: true, deleting: false });
  const [sharedFolders, setSharedFolders] = useState<FolderItem[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [selectedFolders, setSelectedFolders] = useState<Set<string>>(new Set());
  const [deleteFolderConfirm, setDeleteFolderConfirm] = useState<{ open: boolean; folder: FolderItem | null; notEmpty: boolean }>({ open: false, folder: null, notEmpty: false });

  const visibleCategories = CATEGORIES.filter(c => !c.adminOnly || isAdmin);

  const loadQuota = useCallback(async () => {
    try {
      const data = await apiClient.getTopLevel<FileManagerQuotaResponse>('/files/storage/quota');
      if (data.success) setQuota(data.quota);
    } catch (err) {
      console.error('FileManager: load quota error:', err);
    }
  }, []);

  const loadFolderTree = useCallback(async () => {
    try {
      const data = await apiClient.getTopLevel<FileManagerFolderTreeResponse>('/files/folders/tree');
      if (data.success) setFolderTree(data.tree || []);
    } catch (err) {
      console.error('FileManager: load folder tree error:', err);
    }
  }, []);

  const loadFolders = useCallback(async (parentId?: string | null, category?: string | null) => {
    try {
      const qp = new URLSearchParams();
      if (parentId) qp.set('parentId', parentId);
      if (category) qp.set('category', category);
      const qs = qp.toString();
      const data = await apiClient.getTopLevel<FileManagerFoldersResponse>(`/files/folders${qs ? `?${qs}` : ''}`);
      if (data.success) setFolders(data.folders || []);
    } catch (err) {
      console.error('FileManager: load folders error:', err);
    }
  }, []);

  const loadFiles = useCallback(async () => {
    setLoadingFiles(true);
    try {
      const params = new URLSearchParams();
      if (currentCategory) params.set('category', currentCategory);
      if (currentFolderId) params.set('folderId', currentFolderId);
      if (searchTerm) params.set('search', searchTerm);
      const data = await apiClient.getTopLevel<FileManagerFilesResponse>(`/files?${params.toString()}`);
      if (data.success) {
        setFiles(data.files || []);
      }
    } catch (err) {
      console.error('FileManager: load files error:', err);
      setFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }, [currentCategory, currentFolderId, searchTerm]);

  const loadSharedFiles = useCallback(async () => {
    setLoadingFiles(true);
    try {
      const data = await apiClient.getTopLevel<FileManagerSharedResponse>('/files/shared');
      if (data.success) {
        setSharedFiles(data.files || []);
        setSharedFolders(data.folders || []);
      }
    } catch (err) {
      console.error('FileManager: load shared files error:', err);
    } finally {
      setLoadingFiles(false);
    }
  }, []);

  const loadTrash = useCallback(async () => {
    setLoadingFiles(true);
    try {
      const data = await apiClient.getTopLevel<FileManagerFilesResponse>('/files/trash');
      if (data.success) {
        setTrashFiles(data.files || []);
      }
    } catch (err) {
      console.error('FileManager: load trash error:', err);
    } finally {
      setLoadingFiles(false);
    }
  }, []);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.getTopLevel<FileManagerAnalyticsResponse>('/files/storage/analytics');
      if (data.success) setAnalytics(data.analytics);
    } catch (err) {
      console.error('FileManager: load analytics error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const data = await apiClient.getTopLevel<FileManagerSettingsResponse>('/files/storage/settings');
      if (data.success) setStorageSettings(data.settings);
    } catch (err) {
      console.error('FileManager: load settings error:', err);
    }
  }, []);

  const loadDriveAccounts = useCallback(async () => {
    try {
      const data = await apiClient.getTopLevel<GoogleDriveAccountsResponse>('/google-drive/accounts');
      if (data.success && data.accounts) {
        setDriveAccounts(data.accounts);
        const first = data.accounts[0];
        if (first && !selectedDriveAccount) {
          setSelectedDriveAccount(first.id);
        }
      }
    } catch (err) {
      console.error('FileManager: load drive accounts error:', err);
    }
  }, [selectedDriveAccount]);

  const loadDriveFiles = useCallback(async (fId?: string, accountId?: string, search?: string) => {
    setDriveLoading(true);
    try {
      const params = new URLSearchParams();
      if (accountId || selectedDriveAccount) params.set('accountId', accountId || selectedDriveAccount);
      params.set('folderId', fId || driveFolderId);
      if (search) params.set('q', search);
      const data = await apiClient.getTopLevel<GoogleDriveFilesResponse>(`/google-drive/files?${params.toString()}`);
      if (data.success) {
        setDriveFiles(data.files || []);
      }
    } catch (err) {
      console.error('FileManager: load drive files error:', err);
      setDriveFiles([]);
    } finally {
      setDriveLoading(false);
    }
  }, [selectedDriveAccount, driveFolderId]);

  const loadDriveFolders = useCallback(async (parentId?: string, accountId?: string) => {
    try {
      const params = new URLSearchParams();
      const acctId = accountId || selectedDriveAccount;
      if (acctId) params.set('accountId', acctId);
      params.set('parentId', parentId || 'root');
      const data = await apiClient.getTopLevel<GoogleDriveFoldersResponse>(`/google-drive/folders?${params.toString()}`);
      if (data.success) {
        setSyncDriveFolders(data.folders || []);
      }
    } catch (err) {
      console.error('FileManager: load drive folders error:', err);
    }
  }, [selectedDriveAccount]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadQuota(), loadFolderTree(), loadSettings()]).finally(() => setLoading(false));
  }, [loadQuota, loadFolderTree, loadSettings]);

  const loadGroups = useCallback(async () => {
    try {
      const d = await apiClient.get<WorkgroupSummary[]>('/messenger/workgroups');
      if (d.success) setGroupsList(d.data || []);
    } catch { /* silent */ }
  }, []);

  const loadGroupFiles = useCallback(async (wgId: string) => {
    try {
      const d = await apiClient.get<WorkgroupFile[]>(`/messenger/workgroups/${wgId}/files`);
      if (d.success) setGroupFiles(d.data || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (sidebarSection === 'shared') {
      loadSharedFiles();
    } else if (sidebarSection === 'trash') {
      loadTrash();
    } else if (sidebarSection === 'analytics') {
      loadAnalytics();
    } else if (sidebarSection === 'settings') {
      loadSettings();
      if (driveAccounts.length === 0) loadDriveAccounts();
    } else if (sidebarSection === 'drive') {
      if (driveAccounts.length === 0) loadDriveAccounts();
      if (selectedDriveAccount) loadDriveFiles(driveFolderId, selectedDriveAccount, driveSearchTerm || undefined);
    } else if (sidebarSection === 'groups') {
      loadGroups();
      if (selectedGroup) loadGroupFiles(selectedGroup.id);
    } else if (sidebarSection === 'categories') {
      loadFiles();
      loadFolders(currentFolderId, currentCategory);
    }
  }, [sidebarSection, currentCategory, currentFolderId, loadFiles, loadSharedFiles, loadFolders, loadTrash, loadAnalytics, loadSettings, loadDriveFiles, loadDriveAccounts, selectedDriveAccount, driveFolderId, driveSearchTerm, driveAccounts.length, loadGroups, selectedGroup, loadGroupFiles]);

  const clearSelection = () => {
    setSelectedFiles(new Set());
    setSelectedFolders(new Set());
  };

  const navigateToFolder = (folderId: string | null, folderName?: string) => {
    setCurrentFolderId(folderId);
    clearSelection();
    if (folderId === null) {
      setBreadcrumbs([]);
    } else if (folderName) {
      setBreadcrumbs(prev => [...prev, { id: folderId, name: folderName }]);
    }
  };

  const navigateToBreadcrumb = (index: number) => {
    if (index < 0) {
      setCurrentFolderId(null);
      setBreadcrumbs([]);
    } else {
      const crumb = breadcrumbs[index];
      setCurrentFolderId(crumb!.id!);
      setBreadcrumbs(prev => prev.slice(0, index + 1));
    }
    clearSelection();
  };

  const selectCategory = (catId: string) => {
    setCurrentCategory(catId);
    setCurrentFolderId(null);
    setBreadcrumbs([]);
    setSidebarSection('categories');
    clearSelection();
  };

  const handleUpload = async (fileList: FileList | File[]) => {
    if (!currentCategory) return;

    setUploading(true);
    setUploadProgress(0);
    const totalFiles = Array.from(fileList).length;
    let completed = 0;

    try {
      for (const file of Array.from(fileList)) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', currentCategory);
        if (currentFolderId) formData.append('folderId', currentFolderId);

        await apiClient.postTopLevel<FileManagerOkResponse>('/files/upload', formData);

        completed++;
        setUploadProgress(Math.round((completed / totalFiles) * 100));
      }
      await Promise.all([loadFiles(), loadQuota()]);
    } catch (err) {
      console.error('FileManager: upload error:', err);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleUpload(e.target.files);
      e.target.value = '';
    }
  };

  const handleDownload = async (file: FileItem) => {
    try {
      const data = await apiClient.getTopLevel<FileManagerDownloadResponse>(`/files/download/${file.id}`);
      if (data.success && data.downloadUrl) {
        window.open(data.downloadUrl, '_blank');
      } else if (data.success) {
        const { blob } = await apiClient.download(`/files/download/${file.id}`);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.fileName || file.file_name;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('FileManager: download error:', err);
    }
  };

  const handleDelete = async (file: FileItem) => {
    if (!confirm(`Sei sicuro di voler eliminare "${file.file_name}"?`)) return;
    try {
      const data = await apiClient.deleteTopLevel<FileManagerOkResponse>(`/files/${file.id}`);
      if (data.success) {
        await Promise.all([loadFiles(), loadQuota()]);
      }
    } catch (err) {
      console.error('FileManager: delete error:', err);
    }
  };

  const handleRestore = async (file: FileItem) => {
    try {
      const data = await apiClient.postTopLevel<FileManagerOkResponse>(`/files/${file.id}/restore`);
      if (data.success) {
        await loadTrash();
        await loadQuota();
      }
    } catch (err) {
      console.error('FileManager: restore error:', err);
    }
  };

  // Task #1326 — esegue la DELETE definitiva. `cascadeWiki=true` aggiunge
  // `?cascade_wiki=1` al path: il backend cancella le righe wiki sources
  // referenzianti, ri-genera l'INDICE e logga la cronologia PRIMA della
  // delete su R2.
  const performPermanentDelete = async (fileId: string, cascadeWiki: boolean): Promise<boolean> => {
    try {
      const path = cascadeWiki
        ? `/files/${fileId}/permanent?cascade_wiki=1`
        : `/files/${fileId}/permanent`;
      const data = await apiClient.deleteTopLevel<FileManagerOkResponse>(path);
      if (data.success) {
        await loadTrash();
        await loadQuota();
        return true;
      }
      return false;
    } catch (err) {
      console.error('FileManager: permanent delete error:', err);
      return false;
    }
  };

  const handlePermanentDelete = async (file: FileItem) => {
    // Task #1326 — pre-check: se il file è referenziato da almeno 1 fonte
    // wiki, apri il dialog di conferma con la checkbox cascade. Altrimenti
    // comportamento attuale invariato (confirm nativo + delete diretta).
    let impact: WikiFileImpact = { count: 0, sources: [] };
    try {
      const r = await apiClient.get<WikiFileImpact>(`/company-wiki/sources/by-file/${file.id}`);
      if (r?.success && r.data && typeof r.data.count === 'number') {
        impact = r.data;
      }
    } catch (err) {
      console.error('FileManager: wiki-impact check error (non-blocking):', err);
    }
    if (impact.count > 0) {
      setWikiDeleteDialog({ open: true, file, impact, cascade: true, deleting: false });
      return;
    }
    if (!confirm(`Eliminare DEFINITIVAMENTE "${file.file_name}"? Questa azione non può essere annullata.`)) return;
    await performPermanentDelete(file.id, false);
  };

  const handleConfirmWikiDelete = async () => {
    const f = wikiDeleteDialog.file;
    if (!f) return;
    setWikiDeleteDialog((prev) => ({ ...prev, deleting: true }));
    const ok = await performPermanentDelete(f.id, wikiDeleteDialog.cascade);
    if (ok) {
      setWikiDeleteDialog({ open: false, file: null, impact: { count: 0, sources: [] }, cascade: true, deleting: false });
    } else {
      setWikiDeleteDialog((prev) => ({ ...prev, deleting: false }));
    }
  };

  const handleEmptyTrash = async () => {
    if (!confirm('Svuotare completamente il cestino? Tutti i file verranno eliminati definitivamente.')) return;
    try {
      const data = await apiClient.postTopLevel<FileManagerOkResponse>('/files/trash/empty');
      if (data.success) {
        await loadTrash();
        await loadQuota();
      }
    } catch (err) {
      console.error('FileManager: empty trash error:', err);
    }
  };

  const handleRename = async () => {
    if (!renameDialog.file || !newName.trim()) return;
    try {
      const data = await apiClient.patch(`/files/${renameDialog.file.id}`, { newName: newName.trim() });
      if (data.success) {
        await loadFiles();
        setRenameDialog({ open: false, file: null });
        setNewName('');
      }
    } catch (err) {
      console.error('FileManager: rename error:', err);
    }
  };

  const handleMove = async (targetFolderId: string) => {
    if (!moveDialog.file) return;
    try {
      const data = await apiClient.patch(`/files/${moveDialog.file.id}`, { targetFolderId });
      if (data.success) {
        await loadFiles();
        setMoveDialog({ open: false, file: null });
      }
    } catch (err) {
      console.error('FileManager: move error:', err);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const data = await apiClient.post('/files/folders', {
        name: newFolderName.trim(),
        parentId: currentFolderId,
        category: currentCategory,
      });
      if (data.success) {
        await loadFolders(currentFolderId, currentCategory);
        await loadFolderTree();
        setNewFolderDialog(false);
        setNewFolderName('');
      }
    } catch (err) {
      console.error('FileManager: create folder error:', err);
    }
  };

  const toggleFileSelect = (fileId: string) => {
    setSelectedFiles(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  };

  const toggleFolderSelect = (folderId: string) => {
    setSelectedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  };

  const filteredFiles = files.filter(f =>
    f.file_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const displayFiles = sidebarSection === 'shared' ? sharedFiles : sidebarSection === 'trash' ? trashFiles : filteredFiles;

  const selectAll = () => {
    const allFileIds = new Set(displayFiles.map(f => f.id));
    const allFolderIds = new Set(folders.filter(f => !f.is_system).map(f => f.id));
    setSelectedFiles(allFileIds);
    setSelectedFolders(allFolderIds);
  };

  const totalSelected = selectedFiles.size + selectedFolders.size;

  const handleBatchDelete = async () => {
    if (totalSelected === 0) return;
    try {
      const errors: string[] = [];
      for (const fileId of selectedFiles) {
        try {
          await apiClient.deleteTopLevel<FileManagerOkResponse>(`/files/${fileId}`);
        } catch (e) {
          errors.push(`File: ${getErrorMessage(e) || 'errore sconosciuto'}`);
        }
      }
      for (const folderId of selectedFolders) {
        const folder = folders.find(f => f.id === folderId);
        const filesInFolder = await apiClient.getTopLevel<FileManagerFilesResponse>(`/files?folderId=${folderId}`);
        const foldersInFolder = await apiClient.getTopLevel<FileManagerFoldersResponse>(`/files/folders?parentId=${folderId}`);
        if ((filesInFolder.files?.length ?? 0) > 0 || (foldersInFolder.folders?.length ?? 0) > 0) {
          errors.push(`Cartella "${folder?.name || folderId}" non è vuota`);
          continue;
        }
        try {
          await apiClient.deleteTopLevel<FileManagerOkResponse>(`/files/folders/${folderId}`);
        } catch (e) {
          errors.push(`Cartella "${folder?.name}": ${getErrorMessage(e) || 'errore sconosciuto'}`);
        }
      }
      if (errors.length > 0) {
        alert(`Alcuni elementi non sono stati eliminati:\n${errors.join('\n')}`);
      }
      clearSelection();
      await Promise.all([loadFiles(), loadFolders(currentFolderId, currentCategory), loadQuota()]);
    } catch (err) {
      console.error('FileManager: batch delete error:', err);
    }
  };

  const handleBatchDownload = async () => {
    for (const fileId of selectedFiles) {
      const file = files.find(f => f.id === fileId);
      if (file) handleDownload(file);
    }
  };

  const handleDeleteFolder = async (folder: FolderItem) => {
    try {
      const filesInFolder = await apiClient.getTopLevel<FileManagerFilesResponse>(`/files?folderId=${folder.id}`);
      const foldersInFolder = await apiClient.getTopLevel<FileManagerFoldersResponse>(`/files/folders?parentId=${folder.id}`);
      const hasFiles = (filesInFolder.files?.length ?? 0) > 0;
      const hasFolders = (foldersInFolder.folders?.length ?? 0) > 0;

      if (hasFiles || hasFolders) {
        setDeleteFolderConfirm({ open: true, folder, notEmpty: true });
        return;
      }

      setDeleteFolderConfirm({ open: true, folder, notEmpty: false });
    } catch (err) {
      console.error('FileManager: check folder error:', err);
    }
  };

  const confirmDeleteFolder = async () => {
    if (!deleteFolderConfirm.folder) return;
    try {
      await apiClient.deleteTopLevel<FileManagerOkResponse>(`/files/folders/${deleteFolderConfirm.folder.id}`);
      setDeleteFolderConfirm({ open: false, folder: null, notEmpty: false });
      await Promise.all([loadFolders(currentFolderId, currentCategory), loadFolderTree()]);
    } catch (err) {
      console.error('FileManager: delete folder error:', err);
    }
  };

  const handleShare = async () => {
    if ((!shareDialog.file && !shareDialog.folder) || (selectedShareUsers.length === 0 && selectedShareGroups.length === 0)) return;
    try {
      const isFolder = !!shareDialog.folder;
      const itemId = isFolder ? shareDialog.folder!.id! : shareDialog.file!.id!;
      const url = isFolder ? `/files/folders/${itemId}/share` : `/files/${itemId}/share`;
      for (const userId of selectedShareUsers) {
        await apiClient.post(url, {
          sharedWith: userId,
          permission: sharePermission,
          expiresAt: shareExpiry || undefined,
        });
      }
      for (const groupId of selectedShareGroups) {
        const entityId = shareDialog.file?.id || shareDialog.folder?.id;
        const shareGroupUrl = shareDialog.folder
          ? `/files/folders/${entityId}/share-group`
          : `/files/${entityId}/share-group`;
        await apiClient.post(shareGroupUrl, {
          groupId,
          permission: sharePermission,
          expiresAt: shareExpiry || undefined,
        });
      }
      setShareDialog({ open: false, file: null, folder: null });
      setSelectedShareUsers([]);
      setSelectedShareGroups([]);
      setSharePermission('read');
      setShareExpiry('');
    } catch (err) {
      console.error('FileManager: share error:', err);
    }
  };

  const openShareDialog = async (file?: FileItem, folder?: FolderItem) => {
    setShareDialog({ open: true, file: file || null, folder: folder || null });
    try {
      const data = await apiClient.get<TenantUsersListResponse>('/users?limit=100');
      if (data.success) {
        const usersList = data.data?.users ?? [];
        setShareUsers(
          usersList.map((u): ShareUser => ({
            id: u.id,
            name: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email,
            email: u.email,
          })).filter((u) => u.id !== user?.id)
        );
      }
    } catch (err) {
      console.error('FileManager: load users error:', err);
    }
    try {
      const data = await apiClient.get<WorkgroupSummary[]>('/messenger/workgroups');
      if (data.success) setShareGroups(data.data || []);
    } catch { /* silent */ }
  };

  const handleOpenAISummary = async (file: FileItem) => {
    setAiSummaryDialog({ open: true, file, loading: true, summary: null, generatedAt: null });
    try {
      const data = await apiClient.getTopLevel<FileManagerAiSummaryResponse>(`/files/${file.id}/ai-summary`);
      if (data.success && data.summary) {
        setAiSummaryDialog(prev => ({ ...prev, loading: false, summary: data.summary, generatedAt: data.generatedAt }));
      } else {
        const genData = await apiClient.postTopLevel<FileManagerAiSummaryResponse>(`/files/${file.id}/ai-summary`);
        if (genData.success) {
          setAiSummaryDialog(prev => ({ ...prev, loading: false, summary: genData.summary, generatedAt: genData.generatedAt }));
          setFiles(prev => prev.map(f => f.id === file.id ? { ...f, ai_summary: genData.summary, ai_summary_at: genData.generatedAt } : f));
        }
      }
    } catch (err) {
      console.error('FileManager: AI summary error:', err);
      setAiSummaryDialog(prev => ({ ...prev, loading: false, summary: `Errore: ${getErrorMessage(err) || 'Impossibile generare il riassunto'}`, generatedAt: null }));
    }
  };

  const handleRegenerateAISummary = async () => {
    if (!aiSummaryDialog.file) return;
    setAiSummaryDialog(prev => ({ ...prev, loading: true }));
    try {
      const data = await apiClient.postTopLevel<FileManagerAiSummaryResponse>(`/files/${aiSummaryDialog.file.id}/ai-summary`);
      if (data.success) {
        setAiSummaryDialog(prev => ({ ...prev, loading: false, summary: data.summary, generatedAt: data.generatedAt }));
        setFiles(prev => prev.map(f => f.id === aiSummaryDialog.file!.id ? { ...f, ai_summary: data.summary, ai_summary_at: data.generatedAt } : f));
      }
    } catch (err) {
      console.error('FileManager: regenerate AI summary error:', err);
      setAiSummaryDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Task #2834 — Azioni file di gruppo (Download / Rinomina / Riassunto AI /
  // Elimina). Endpoint dedicati su /api/messenger/workgroups/:id/files/:fileId,
  // tabella separata (`messenger_workgroup_files`). Riusano i dialog esistenti.
  const handleGroupDownload = async (file: GroupFile) => {
    if (!selectedGroup) return;
    try {
      const data = await apiClient.getTopLevel<FileManagerDownloadResponse>(`/messenger/workgroups/${selectedGroup.id}/files/${file.id}/download`);
      if (data.success && data.downloadUrl) {
        window.open(data.downloadUrl, '_blank');
      }
    } catch (err) {
      console.error('FileManager: group download error:', err);
    }
  };

  const handleGroupRename = async () => {
    if (!groupRenameDialog.file || !selectedGroup || !groupNewName.trim()) return;
    try {
      const data = await apiClient.patch<WorkgroupFile>(`/messenger/workgroups/${selectedGroup.id}/files/${groupRenameDialog.file.id}`, { newName: groupNewName.trim() });
      if (data.success) {
        await loadGroupFiles(selectedGroup.id);
        setGroupRenameDialog({ open: false, file: null });
        setGroupNewName('');
      }
    } catch (err) {
      console.error('FileManager: group rename error:', err);
    }
  };

  const handleGroupOpenAISummary = async (file: GroupFile) => {
    if (!selectedGroup) return;
    const wgId = selectedGroup.id;
    setGroupAiSummaryDialog({ open: true, file, loading: true, summary: null, generatedAt: null });
    try {
      const data = await apiClient.getTopLevel<FileManagerAiSummaryResponse>(`/messenger/workgroups/${wgId}/files/${file.id}/ai-summary`);
      if (data.success && data.summary) {
        setGroupAiSummaryDialog(prev => ({ ...prev, loading: false, summary: data.summary, generatedAt: data.generatedAt }));
      } else {
        const genData = await apiClient.postTopLevel<FileManagerAiSummaryResponse>(`/messenger/workgroups/${wgId}/files/${file.id}/ai-summary`);
        if (genData.success) {
          setGroupAiSummaryDialog(prev => ({ ...prev, loading: false, summary: genData.summary, generatedAt: genData.generatedAt }));
          setGroupFiles(prev => prev.map(f => f.id === file.id ? { ...f, ai_summary: genData.summary, ai_summary_at: genData.generatedAt } : f));
        }
      }
    } catch (err) {
      console.error('FileManager: group AI summary error:', err);
      setGroupAiSummaryDialog(prev => ({ ...prev, loading: false, summary: `Errore: ${getErrorMessage(err) || 'Impossibile generare il riassunto'}`, generatedAt: null }));
    }
  };

  const handleGroupRegenerateAISummary = async () => {
    if (!groupAiSummaryDialog.file || !selectedGroup) return;
    const fileId = groupAiSummaryDialog.file.id;
    setGroupAiSummaryDialog(prev => ({ ...prev, loading: true }));
    try {
      const data = await apiClient.postTopLevel<FileManagerAiSummaryResponse>(`/messenger/workgroups/${selectedGroup.id}/files/${fileId}/ai-summary`);
      if (data.success) {
        setGroupAiSummaryDialog(prev => ({ ...prev, loading: false, summary: data.summary, generatedAt: data.generatedAt }));
        setGroupFiles(prev => prev.map(f => f.id === fileId ? { ...f, ai_summary: data.summary, ai_summary_at: data.generatedAt } : f));
      }
    } catch (err) {
      console.error('FileManager: group regenerate AI summary error:', err);
      setGroupAiSummaryDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const handleGroupDelete = async (file: GroupFile) => {
    if (!selectedGroup) return;
    if (!confirm(`Sei sicuro di voler eliminare "${file.file_name}"?`)) return;
    try {
      const data = await apiClient.delete(`/messenger/workgroups/${selectedGroup.id}/files/${file.id}`);
      if (data.success) {
        await loadGroupFiles(selectedGroup.id);
      }
    } catch (err) {
      console.error('FileManager: group delete error:', err);
    }
  };

  const handleSaveSettings = async () => {
    if (!storageSettings) return;
    setSettingsSaving(true);
    try {
      const data = await apiClient.patchTopLevel<FileManagerSettingsResponse>('/files/storage/settings', storageSettings);
      if (data.success) {
        setStorageSettings(data.settings);
        await loadQuota();
      }
    } catch (err) {
      console.error('FileManager: save settings error:', err);
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.types.includes('Files')) setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragOver(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragOver(false);
    if (!currentCategory) return;
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) await handleUpload(droppedFiles);
  };

  const navigateDriveFolder = (fId: string, name: string) => {
    setDriveFolderId(fId);
    setDriveBreadcrumbs(prev => [...prev, { id: fId, name }]);
  };

  const navigateDriveBreadcrumb = (index: number) => {
    if (index < 0) {
      setDriveFolderId('root');
      setDriveBreadcrumbs([]);
    } else {
      const crumb = driveBreadcrumbs[index];
      setDriveFolderId(crumb!.id!);
      setDriveBreadcrumbs(prev => prev.slice(0, index + 1));
    }
  };

  const handleSyncToDrive = async () => {
    if (!syncToDriveDialog.file) return;
    setSyncDriveLoading(true);
    try {
      const data = await apiClient.postTopLevel<FileManagerOkResponse>('/google-drive/upload', {
        fileId: syncToDriveDialog.file.id,
        driveFolderId: syncDriveFolderId || undefined,
        accountId: selectedDriveAccount || undefined,
      });
      if (data.success) {
        await loadFiles();
        setSyncToDriveDialog({ open: false, file: null });
        setSyncDriveFolderId('');
      }
    } catch (err) {
      console.error('FileManager: sync to drive error:', err);
      alert(getErrorMessage(err) || 'Errore durante la sincronizzazione');
    } finally {
      setSyncDriveLoading(false);
    }
  };

  const handleImportFromDrive = async () => {
    if (!importFromDriveDialog.driveFile) return;
    setImportLoading(true);
    try {
      const data = await apiClient.postTopLevel<FileManagerOkResponse>('/google-drive/import', {
        driveFileId: importFromDriveDialog.driveFile.id,
        category: importCategory,
        folderId: importFolderId || null,
        accountId: selectedDriveAccount || undefined,
      });
      if (data.success) {
        setImportFromDriveDialog({ open: false, driveFile: null });
        setImportCategory('document');
        setImportFolderId('');
        await Promise.all([loadFiles(), loadQuota()]);
      }
    } catch (err) {
      console.error('FileManager: import from drive error:', err);
      alert(getErrorMessage(err) || 'Errore durante l\'importazione');
    } finally {
      setImportLoading(false);
    }
  };

  const openSyncToDriveDialog = async (file: FileItem) => {
    setSyncToDriveDialog({ open: true, file });
    setSyncDriveFolderId('');
    if (driveAccounts.length === 0) await loadDriveAccounts();
    await loadDriveFolders();
  };

  const getDriveFileIcon = (mimeType: string, isFolder: boolean) => {
    if (isFolder) return <FolderOpen className="h-5 w-5 text-yellow-500" />;
    if (mimeType?.startsWith('video/')) return <Video className="h-5 w-5 text-purple-500" />;
    if (mimeType?.startsWith('audio/')) return <Mic className="h-5 w-5 text-green-500" />;
    if (mimeType?.startsWith('image/')) return <File className="h-5 w-5 text-pink-500" />;
    if (mimeType?.includes('pdf')) return <FileText className="h-5 w-5 text-red-500" />;
    if (mimeType?.includes('spreadsheet') || mimeType?.includes('excel')) return <FileText className="h-5 w-5 text-green-600 dark:text-green-400" />;
    if (mimeType?.includes('presentation') || mimeType?.includes('powerpoint')) return <FileText className="h-5 w-5 text-orange-500" />;
    if (mimeType?.includes('document') || mimeType?.includes('word')) return <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />;
    return <FileText className="h-5 w-5 text-blue-500" />;
  };

  const quotaPercentage = quota ? Math.min(100, Math.round((quota.used_bytes / quota.max_bytes) * 100)) : 0;

  const categoryBreakdown = quota ? [
    { label: 'Documenti', bytes: quota.documents_used_bytes || 0, color: 'bg-blue-500' },
    { label: 'Video', bytes: quota.videos_used_bytes || 0, color: 'bg-purple-500' },
    { label: 'Registrazioni', bytes: quota.recordings_used_bytes || 0, color: 'bg-green-500' },
  ] : [];

  const renderBreadcrumb = () => {
    const segmentClass = "flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-colors shrink-0";
    const activeSegment = `${segmentClass} font-semibold text-foreground bg-blue-50 dark:bg-blue-900/30`;
    const clickableSegment = `${segmentClass} text-muted-foreground hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-700 dark:hover:text-blue-300 cursor-pointer`;
    const separatorEl = <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0 mx-0.5" />;

    const wrapperClass = "flex items-center text-sm bg-card border border-border rounded-lg px-2 py-1 overflow-x-auto shadow-sm";

    if (sidebarSection === 'shared') {
      return (
        <div className={wrapperClass}>
          <div className={activeSegment}>
            <Share2 className="h-4 w-4 text-blue-500" />
            <span>Condivisi con me</span>
          </div>
        </div>
      );
    }
    if (sidebarSection === 'trash') {
      return (
        <div className={wrapperClass}>
          <div className={activeSegment}>
            <Trash2 className="h-4 w-4 text-red-500" />
            <span>Cestino</span>
          </div>
        </div>
      );
    }
    if (sidebarSection === 'analytics') {
      return (
        <div className={wrapperClass}>
          <div className={activeSegment}>
            <BarChart3 className="h-4 w-4 text-blue-500" />
            <span>Analytics</span>
          </div>
        </div>
      );
    }
    if (sidebarSection === 'settings') {
      return (
        <div className={wrapperClass}>
          <div className={activeSegment}>
            <Settings className="h-4 w-4 text-muted-foreground" />
            <span>Impostazioni Storage</span>
          </div>
        </div>
      );
    }
    if (sidebarSection === 'groups') {
      return (
        <div className={wrapperClass}>
          {selectedGroup ? (
            <>
              <button className={clickableSegment} onClick={() => setSelectedGroup(null)}>
                <Users className="h-4 w-4 text-green-500" />
                <span>Gruppi</span>
              </button>
              {separatorEl}
              <div className={activeSegment}>
                <span>{selectedGroup.name}</span>
              </div>
            </>
          ) : (
            <div className={activeSegment}>
              <Users className="h-4 w-4 text-green-500" />
              <span>File dei Gruppi</span>
            </div>
          )}
        </div>
      );
    }
    if (sidebarSection === 'drive') {
      return (
        <div className={wrapperClass}>
          <button
            onClick={() => { setDriveFolderId('root'); setDriveBreadcrumbs([]); }}
            className={driveBreadcrumbs.length === 0 ? activeSegment : clickableSegment}
          >
            <svg className="h-4 w-4" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
              <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
              <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-20.4 35.3c-.8 1.4-1.2 2.95-1.2 4.5h27.5z" fill="#00ac47"/>
              <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l11.752 23.8z" fill="#ea4335"/>
              <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
              <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/>
              <path d="m73.4 26.5-10.2-17.7c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 23.8h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/>
            </svg>
            <span>Google Drive</span>
          </button>
          {driveBreadcrumbs.map((crumb, i) => {
            const isLast = i === driveBreadcrumbs.length - 1;
            return (
              <span key={crumb.id} className="flex items-center shrink-0">
                {separatorEl}
                <button
                  onClick={() => navigateDriveBreadcrumb(i)}
                  className={isLast ? activeSegment : clickableSegment}
                >
                  <Folder className="h-4 w-4 text-yellow-500" />
                  <span>{crumb.name}</span>
                </button>
              </span>
            );
          })}
        </div>
      );
    }

    const catInfo = currentCategory ? CATEGORIES.find(c => c.id === currentCategory) : null;
    const isAtCategoryRoot = currentCategory && breadcrumbs.length === 0;

    return (
      <div className={wrapperClass}>
        <button
          onClick={() => { setCurrentCategory(null); setCurrentFolderId(null); setBreadcrumbs([]); setSidebarSection('categories'); clearSelection(); }}
          className={!currentCategory ? activeSegment : clickableSegment}
        >
          <HardDrive className="h-4 w-4 text-muted-foreground" />
          <span>File Manager</span>
        </button>
        {currentCategory && (
          <>
            {separatorEl}
            <button
              onClick={() => { setCurrentFolderId(null); setBreadcrumbs([]); }}
              className={isAtCategoryRoot ? activeSegment : clickableSegment}
            >
              {catInfo && <catInfo.icon className={`h-4 w-4 ${catInfo.color}`} />}
              <span>{getCategoryLabel(currentCategory)}</span>
            </button>
          </>
        )}
        {breadcrumbs.map((crumb, i) => {
          const isLast = i === breadcrumbs.length - 1;
          return (
            <span key={crumb.id} className="flex items-center shrink-0">
              {separatorEl}
              <button
                onClick={() => navigateToBreadcrumb(i)}
                className={isLast ? activeSegment : clickableSegment}
              >
                <Folder className="h-4 w-4 text-yellow-500" />
                <span>{crumb.name}</span>
              </button>
            </span>
          );
        })}
      </div>
    );
  };

  const renderAnalytics = () => {
    if (loading) return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

    if (!analytics) return (
      <div className="flex flex-col items-center justify-center py-12">
        <BarChart3 className="h-12 w-12 text-muted-foreground/60 mb-4" />
        <p className="text-muted-foreground">Nessun dato analytics disponibile</p>
      </div>
    );

    const totalFiles = analytics.categoryBreakdown.reduce((sum, c) => sum + parseInt(c.file_count), 0);
    const totalBytes = analytics.categoryBreakdown.reduce((sum, c) => sum + parseInt(c.total_bytes), 0);
    const activeCategoriesCount = analytics.categoryBreakdown.filter(c => parseInt(c.file_count) > 0).length;
    const lastUpload = analytics.topFiles.length > 0
      ? analytics.topFiles.reduce((latest, f) => new Date(f.created_at) > new Date(latest.created_at) ? f : latest)
      : null;
    const quotaRadius = 40;
    const quotaCircumference = 2 * Math.PI * quotaRadius;
    const quotaStroke = quotaCircumference - (quotaPercentage / 100) * quotaCircumference;
    const quotaColor = quotaPercentage > 80 ? 'text-red-500' : quotaPercentage > 50 ? 'text-yellow-500' : 'text-green-500';
    const quotaStrokeColor = quotaPercentage > 80 ? '#ef4444' : quotaPercentage > 50 ? '#eab308' : '#22c55e';

    return (
      <div className="space-y-6 p-4">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">File totali</p>
                  <p className="text-2xl font-bold text-foreground mt-1">{totalFiles}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Spazio usato</p>
                  <p className="text-2xl font-bold text-foreground mt-1">{formatBytes(totalBytes)}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center">
                  <Database className="h-5 w-5 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Quota totale</p>
                  <p className="text-2xl font-bold text-foreground mt-1">{quota ? formatBytes(quota.max_bytes) : '-'}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-green-50 dark:bg-green-900/30 flex items-center justify-center">
                  <HardDrive className="h-5 w-5 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-cyan-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Categorie attive</p>
                  <p className="text-2xl font-bold text-foreground mt-1">{activeCategoriesCount}</p>
                  <p className="text-xs text-muted-foreground">di {CATEGORIES.length} totali</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-cyan-50 dark:bg-cyan-900/30 flex items-center justify-center">
                  <Folder className="h-5 w-5 text-cyan-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-orange-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Ultimo upload</p>
                  <p className="text-sm font-semibold text-foreground mt-1 truncate max-w-[120px]" title={lastUpload?.file_name || '-'}>
                    {lastUpload ? lastUpload.file_name : '-'}
                  </p>
                  <p className="text-xs text-muted-foreground">{lastUpload ? new Date(lastUpload.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }) : ''}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-orange-50 dark:bg-orange-900/30 flex items-center justify-center">
                  <ArrowUpRight className="h-5 w-5 text-orange-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-1">
            <CardContent className="p-5">
              <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-blue-500" />
                Utilizzo quota
              </h3>
              <div className="flex flex-col items-center">
                <div className="relative">
                  <svg width="100" height="100" className="transform -rotate-90">
                    <circle cx="50" cy="50" r={quotaRadius} fill="none" stroke="currentColor" strokeWidth="8" className="text-muted" />
                    <circle cx="50" cy="50" r={quotaRadius} fill="none" stroke={quotaStrokeColor} strokeWidth="8" strokeLinecap="round"
                      strokeDasharray={quotaCircumference} strokeDashoffset={quotaStroke}
                      className="transition-all duration-700" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`text-xl font-bold ${quotaColor}`}>{quotaPercentage}%</span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-3">{formatBytes(totalBytes)} di {quota ? formatBytes(quota.max_bytes) : '-'}</p>
              </div>
              {storageSettings && (
                <div className="mt-5 pt-4 border-t border-border space-y-2">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Retention attiva</h4>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Mic className="h-3.5 w-3.5 text-green-500" />
                      <span className="text-muted-foreground">Registrazioni</span>
                    </div>
                    <span className="font-medium text-foreground">{storageSettings.recordings_retention_days || 90}gg</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-3.5 w-3.5 text-yellow-500" />
                      <span className="text-muted-foreground">Soglia allarme</span>
                    </div>
                    <span className="font-medium text-foreground">{storageSettings.alert_threshold_pct || 80}%</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardContent className="p-5">
              <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <Database className="h-4 w-4 text-purple-500" />
                Distribuzione per categoria
              </h3>
              <div className="space-y-4">
                {analytics.categoryBreakdown.map((cat) => {
                  const catInfo = CATEGORIES.find(c => c.id === cat.category);
                  const pctOfTotal = totalBytes > 0 ? Math.round((parseInt(cat.total_bytes) / totalBytes) * 100) : 0;
                  const pctOfQuota = quota && quota.max_bytes > 0 ? ((parseInt(cat.total_bytes) / quota.max_bytes) * 100).toFixed(2) : '0';
                  return (
                    <div key={cat.category} className="p-3 rounded-lg border border-border hover:border-border transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2.5">
                          <div className={`h-8 w-8 rounded-lg ${catInfo?.bgColor || 'bg-muted'} flex items-center justify-center`}>
                            {catInfo && <catInfo.icon className={`h-4 w-4 ${catInfo.color}`} />}
                          </div>
                          <div>
                            <span className="font-medium text-foreground text-sm">{catInfo?.label || cat.category}</span>
                            <p className="text-xs text-muted-foreground">{cat.file_count} file</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-semibold text-foreground">{formatBytes(parseInt(cat.total_bytes))}</span>
                          <p className="text-xs text-muted-foreground">{pctOfQuota}% della quota</p>
                        </div>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-500 ${catInfo?.color?.replace('text-', 'bg-') || 'bg-muted-foreground'}`}
                          style={{ width: `${Math.max(pctOfTotal, pctOfTotal > 0 ? 2 : 0)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
                {analytics.categoryBreakdown.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">Nessun file presente</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardContent className="p-5">
              <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-blue-500" />
                Trend mensile
              </h3>
              {analytics.monthlyTrend.length > 0 ? (
                <div className="flex items-end gap-1.5 h-40">
                  {analytics.monthlyTrend.map((m) => {
                    const maxBytes = Math.max(...analytics.monthlyTrend.map(t => parseInt(t.total_bytes)));
                    const pct = maxBytes > 0 ? Math.max(4, Math.round((parseInt(m.total_bytes) / maxBytes) * 100)) : 4;
                    const monthShort = new Date(m.month).toLocaleDateString('it-IT', { month: 'short' }).substring(0, 3);
                    return (
                      <div key={m.month} className="flex-1 flex flex-col items-center gap-1 group" title={`${new Date(m.month).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}: ${m.file_count} file, ${formatBytes(parseInt(m.total_bytes))}`}>
                        <div className="w-full flex flex-col items-center justify-end h-28">
                          <span className="text-[10px] text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mb-1">
                            {formatBytes(parseInt(m.total_bytes))}
                          </span>
                          <div
                            className="w-full max-w-[28px] bg-gradient-to-t from-blue-500 to-blue-400 rounded-t-md transition-all duration-300 group-hover:from-blue-600 group-hover:to-blue-500"
                            style={{ height: `${pct}%` }}
                          />
                        </div>
                        <span className="text-[10px] text-muted-foreground capitalize">{monthShort}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">Nessun trend disponibile</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <File className="h-4 w-4 text-green-500" />
                File più grandi
              </h3>
              {analytics.topFiles.length > 0 ? (
                <div className="space-y-2">
                  {analytics.topFiles.map((file, i) => (
                    <div key={file.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50 transition-colors">
                      <div className={`h-7 w-7 rounded-lg flex items-center justify-center text-xs font-bold ${
                        i === 0 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' : i === 1 ? 'bg-muted text-muted-foreground' : i === 2 ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' : 'bg-muted/50 text-muted-foreground'
                      }`}>
                        {i + 1}
                      </div>
                      {getFileIcon(file.category)}
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-foreground block truncate">{file.file_name}</span>
                        <span className="text-xs text-muted-foreground">{getCategoryLabel(file.category)}</span>
                      </div>
                      <span className="text-sm font-semibold text-foreground shrink-0">{formatBytes(file.file_size)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">Nessun file presente</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  const renderSettings = () => {
    if (!storageSettings) return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

    return (
      <div className="space-y-6 p-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2.5">
                <div className="h-8 w-8 rounded-lg bg-green-50 dark:bg-green-900/30 flex items-center justify-center">
                  <Mic className="h-4 w-4 text-green-500" />
                </div>
                <h3 className="font-semibold text-foreground">Registrazioni</h3>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div>
                  <p className="text-sm font-medium text-foreground">Abilita registrazioni cloud</p>
                  <p className="text-xs text-muted-foreground">Sincronizza automaticamente le registrazioni su cloud</p>
                </div>
                <button
                  onClick={() => setStorageSettings((s) => ({ ...(s ?? {}), recordings_enabled: !s?.recordings_enabled }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${storageSettings.recordings_enabled ? 'bg-green-500' : 'bg-muted-foreground/30'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-card transition-transform ${storageSettings.recordings_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Limite storage (GB)</label>
                  <Input
                    type="number"
                    value={Math.round((storageSettings.recordings_max_bytes || 0) / (1024 * 1024 * 1024))}
                    onChange={(e) => setStorageSettings((s) => ({ ...(s ?? {}), recordings_max_bytes: parseInt(e.target.value) * 1024 * 1024 * 1024 }))}
                    className="mt-1 h-9"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Retention (giorni)</label>
                  <Input
                    type="number"
                    value={storageSettings.recordings_retention_days || 90}
                    onChange={(e) => setStorageSettings((s) => ({ ...(s ?? {}), recordings_retention_days: parseInt(e.target.value) }))}
                    className="mt-1 h-9"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

        </div>

        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 flex items-center justify-center">
                <AlertTriangle className="h-4 w-4 text-yellow-500" />
              </div>
              <h3 className="font-semibold text-foreground">Soglia di allarme</h3>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex-1 max-w-xs">
                <label className="text-xs font-medium text-muted-foreground">Percentuale di utilizzo per allarme (%)</label>
                <Input
                  type="number"
                  min="50"
                  max="99"
                  value={storageSettings.alert_threshold_pct || 80}
                  onChange={(e) => setStorageSettings((s) => ({ ...(s ?? {}), alert_threshold_pct: parseInt(e.target.value) }))}
                  className="mt-1 h-9"
                />
              </div>
              <p className="text-xs text-muted-foreground flex-1 pt-4">Riceverai una notifica quando lo storage supera questa soglia</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-purple-500" />
              </div>
              <h3 className="font-semibold text-foreground">Riassunto AI</h3>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div>
                <p className="text-sm font-medium text-foreground">Abilita riassunto AI dei file</p>
                <p className="text-xs text-muted-foreground">Genera riassunti automatici dei documenti usando l'AI</p>
              </div>
              <button
                onClick={() => setStorageSettings((s) => ({ ...(s ?? {}), ai_summary_enabled: !s?.ai_summary_enabled }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${storageSettings.ai_summary_enabled ? 'bg-purple-500' : 'bg-muted-foreground/30'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-card transition-transform ${storageSettings.ai_summary_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="h-8 w-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                  <Cloud className="h-4 w-4 text-blue-500" />
                </div>
                <h3 className="font-semibold text-foreground">Google Drive</h3>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    const data = await apiClient.get<GoogleOauthStartData>(`/integrations/google/oauth/start?scopes=drive&new_account=true`);
                    if (data.success && data.data?.authUrl) window.location.href = data.data.authUrl;
                    else alert(data.error || 'Configura prima Client ID e Client Secret nelle Integrazioni');
                  } catch (e) { alert(getErrorMessage(e) || 'Errore durante la connessione Google'); }
                }}
              >
                <Plus className="h-4 w-4 mr-1" /> Aggiungi account
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">Account Google collegati per l'integrazione con Google Drive.</p>
            {driveAccounts.length === 0 ? (
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <Cloud className="h-8 w-8 text-muted-foreground/60 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Nessun account Google collegato.</p>
                <p className="text-xs text-muted-foreground mt-1">Clicca "Aggiungi account" per collegare un account Google con accesso Drive.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {driveAccounts.map(acc => {
                  const hasDriveScope = acc.config?.enabled_scopes?.includes('drive');
                  return (
                    <div key={acc.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                          <svg className="h-4 w-4" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
                            <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
                            <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-20.4 35.3c-.8 1.4-1.2 2.95-1.2 4.5h27.5z" fill="#00ac47"/>
                            <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l11.752 23.8z" fill="#ea4335"/>
                            <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
                            <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/>
                            <path d="m73.4 26.5-10.2-17.7c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 23.8h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/>
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{acc.account_label || acc.account_email || 'Account Google'}</p>
                          {acc.account_email && <p className="text-xs text-muted-foreground">{acc.account_email}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {hasDriveScope ? (
                          <span className="px-2 py-1 text-xs rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">Drive attivo</span>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800 hover:bg-amber-50 dark:hover:bg-amber-900/20 text-xs h-7"
                            onClick={async () => {
                              try {
                                const currentScopes = acc.config?.enabled_scopes || [];
                                const newScopes = [...new Set([...currentScopes, 'drive'])];
                                const data = await apiClient.get<GoogleOauthStartData>(`/integrations/google/oauth/start?scopes=${newScopes.join(',')}&account_id=${acc.id}`);
                                if (data.success && data.data?.authUrl) window.location.href = data.data.authUrl;
                                else alert(data.error || 'Errore');
                              } catch (e) { alert(getErrorMessage(e) || 'Errore durante la ri-autorizzazione'); }
                            }}
                          >
                            <RefreshCw className="h-3 w-3 mr-1" /> Autorizza Drive
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs h-7"
                          onClick={async () => {
                            if (!confirm(`Disconnettere l'account ${acc.account_email || acc.account_label || ''}?`)) return;
                            try {
                              const data = await apiClient.delete(`/integrations/google?account_id=${acc.id}`);
                              if (data.success) {
                                setDriveAccounts(prev => prev.filter(a => a.id !== acc.id));
                              } else {
                                alert(data.error || 'Errore durante la disconnessione');
                              }
                            } catch (e) { alert(getErrorMessage(e) || 'Errore durante la disconnessione'); }
                          }}
                        >
                          <X className="h-3 w-3 mr-1" /> Disconnetti
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={loadSettings}>Annulla</Button>
          <Button onClick={handleSaveSettings} disabled={settingsSaving}>
            {settingsSaving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            Salva impostazioni
          </Button>
        </div>
      </div>
    );
  };

  const renderFileActions = (file: FileItem, isTrashView: boolean) => {
    if (isTrashView) {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-1 rounded hover:bg-muted">
              <MoreVertical className="h-4 w-4 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleRestore(file)}>
              <RotateCcw className="h-4 w-4 mr-2" /> Ripristina
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handlePermanentDelete(file)} className="text-red-600 dark:text-red-400">
              <X className="h-4 w-4 mr-2" /> Elimina definitivamente
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="p-1 rounded hover:bg-muted opacity-0 group-hover:opacity-100 transition-opacity">
            <MoreVertical className="h-4 w-4 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => handleDownload(file)}>
            <Download className="h-4 w-4 mr-2" /> Download
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => { setRenameDialog({ open: true, file }); setNewName(file.file_name); }}>
            <Edit2 className="h-4 w-4 mr-2" /> Rinomina
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setMoveDialog({ open: true, file })}>
            <Move className="h-4 w-4 mr-2" /> Sposta
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => openShareDialog(file)}>
            <Share2 className="h-4 w-4 mr-2" /> Condividi
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => handleOpenAISummary(file)}
            disabled={!storageSettings?.ai_summary_enabled && !quota?.ai_summary_enabled}
            className={!storageSettings?.ai_summary_enabled && !quota?.ai_summary_enabled ? 'opacity-50' : ''}
          >
            <Sparkles className="h-4 w-4 mr-2" /> {file.ai_summary ? 'Vedi Riassunto AI' : 'Genera Riassunto AI'}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => openSyncToDriveDialog(file)}>
            <Cloud className="h-4 w-4 mr-2" /> Sincronizza su Google Drive
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => handleDelete(file)} className="text-red-600 dark:text-red-400">
            <Trash2 className="h-4 w-4 mr-2" /> Elimina
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const isTrashView = sidebarSection === 'trash';
  const isSpecialView = sidebarSection === 'analytics' || sidebarSection === 'settings' || sidebarSection === 'drive' || sidebarSection === 'groups';

  return (
    <div className="flex h-[calc(100vh-120px)] bg-muted/50 rounded-lg overflow-hidden border">
      <div className="w-64 bg-card border-r flex flex-col">
        <div className="p-4 border-b">
          <h2 className="font-semibold text-foreground flex items-center gap-2">
            <HardDrive className="h-5 w-5 text-muted-foreground" />
            File Manager
          </h2>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          <div className="space-y-1">
            {visibleCategories.map((cat) => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => selectCategory(cat.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left ${
                    currentCategory === cat.id && sidebarSection === 'categories'
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'hover:bg-muted text-foreground'
                  }`}
                >
                  <Icon className={`h-5 w-5 ${cat.color}`} />
                  <span className="font-medium text-sm">{cat.label}</span>
                </button>
              );
            })}
          </div>

          <div className="my-3 border-t" />

          <div className="space-y-1">
            <button
              onClick={() => { setSidebarSection('shared'); setCurrentCategory(null); clearSelection(); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left ${
                sidebarSection === 'shared'
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                  : 'hover:bg-muted text-foreground'
              }`}
            >
              <Share2 className="h-5 w-5 text-indigo-500" />
              <span className="font-medium text-sm">Condivisi con me</span>
            </button>
            <button
              onClick={() => { setSidebarSection('groups'); setCurrentCategory(null); setSelectedGroup(null); clearSelection(); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left ${
                sidebarSection === 'groups'
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                  : 'hover:bg-muted text-foreground'
              }`}
            >
              <Users className="h-5 w-5 text-green-500" />
              <span className="font-medium text-sm">Gruppi</span>
            </button>
            <button
              onClick={() => { setSidebarSection('trash'); setCurrentCategory(null); clearSelection(); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left ${
                sidebarSection === 'trash'
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                  : 'hover:bg-muted text-foreground'
              }`}
            >
              <Trash2 className="h-5 w-5 text-red-500" />
              <span className="font-medium text-sm">Cestino</span>
              {trashFiles.length > 0 && (
                <Badge variant="secondary" className="ml-auto text-[10px]">{trashFiles.length}</Badge>
              )}
            </button>
          </div>

          <div className="my-3 border-t" />

          <div className="space-y-1">
            <button
              onClick={() => { setSidebarSection('drive'); setCurrentCategory(null); clearSelection(); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left ${
                sidebarSection === 'drive'
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                  : 'hover:bg-muted text-foreground'
              }`}
            >
              <svg className="h-5 w-5" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
                <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
                <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-20.4 35.3c-.8 1.4-1.2 2.95-1.2 4.5h27.5z" fill="#00ac47"/>
                <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l11.752 23.8z" fill="#ea4335"/>
                <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
                <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/>
                <path d="m73.4 26.5-10.2-17.7c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 23.8h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/>
              </svg>
              <span className="font-medium text-sm">Google Drive</span>
            </button>
          </div>

          {isAdmin && (
            <>
              <div className="my-3 border-t" />
              <div className="space-y-1">
                <button
                  onClick={() => { setSidebarSection('analytics'); setCurrentCategory(null); clearSelection(); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left ${
                    sidebarSection === 'analytics'
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'hover:bg-muted text-foreground'
                  }`}
                >
                  <BarChart3 className="h-5 w-5 text-blue-500" />
                  <span className="font-medium text-sm">Analytics</span>
                </button>
                <button
                  onClick={() => { setSidebarSection('settings'); setCurrentCategory(null); clearSelection(); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left ${
                    sidebarSection === 'settings'
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      : 'hover:bg-muted text-foreground'
                  }`}
                >
                  <Settings className="h-5 w-5 text-muted-foreground" />
                  <span className="font-medium text-sm">Impostazioni</span>
                </button>
              </div>
            </>
          )}
        </div>

        {quota && (
          <div className="p-3 border-t space-y-2">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Storage</span>
              <span>{formatBytes(quota.used_bytes)} / {formatBytes(quota.max_bytes)}</span>
            </div>
            <Progress value={quotaPercentage} className="h-2" />
            <div className="space-y-1">
              {categoryBreakdown.filter(c => c.bytes > 0).map((c) => (
                <div key={c.label} className="flex items-center justify-between text-[11px] text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <div className={`h-2 w-2 rounded-full ${c.color}`} />
                    <span>{c.label}</span>
                  </div>
                  <span>{formatBytes(c.bytes)}</span>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-muted-foreground text-center">{quotaPercentage}% usato</div>
          </div>
        )}
      </div>

      <div className="flex-1 flex flex-col">
        <div className="bg-card border-b px-4 py-3 flex items-center gap-4">
          {renderBreadcrumb()}

          <div className="flex-1" />

          {sidebarSection === 'drive' ? (
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Cerca in Drive..."
                value={driveSearchTerm}
                onChange={(e) => setDriveSearchTerm(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') loadDriveFiles(driveFolderId, selectedDriveAccount, driveSearchTerm || undefined); }}
                className="pl-9 h-9"
              />
            </div>
          ) : !isSpecialView && (
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Cerca file..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
          )}

          {sidebarSection === 'trash' && trashFiles.length > 0 && isAdmin && (
            <Button size="sm" variant="outline" className="text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={handleEmptyTrash}>
              <Trash2 className="h-4 w-4 mr-2" />
              Svuota cestino
            </Button>
          )}

          {currentCategory && sidebarSection === 'categories' && (
            <>
              <Button size="sm" variant="outline" onClick={() => setNewFolderDialog(true)}>
                <FolderPlus className="h-4 w-4 mr-2" />
                Nuova cartella
              </Button>
              <div className="relative">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileInputChange}
                  multiple
                />
                <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                  <Upload className="h-4 w-4 mr-2" />
                  {uploading ? 'Caricamento...' : 'Carica'}
                </Button>
              </div>
            </>
          )}

          {currentCategory && sidebarSection === 'categories' && (
            <Button size="sm" variant="ghost" onClick={totalSelected > 0 ? clearSelection : selectAll}>
              <Check className="h-4 w-4 mr-1" />
              {totalSelected > 0 ? 'Deseleziona' : 'Seleziona tutto'}
            </Button>
          )}

          {(!isSpecialView || sidebarSection === 'drive') && (
            <div className="flex items-center border rounded-lg">
              <button onClick={() => setViewMode('grid')} className={`p-2 ${viewMode === 'grid' ? 'bg-muted' : ''}`}>
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button onClick={() => setViewMode('list')} className={`p-2 ${viewMode === 'list' ? 'bg-muted' : ''}`}>
                <List className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {totalSelected > 0 && (
          <div className="bg-blue-50 dark:bg-blue-900/30 border-b px-4 py-2 flex items-center gap-3">
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
              {totalSelected} element{totalSelected > 1 ? 'i' : 'o'} selezionat{totalSelected > 1 ? 'i' : 'o'}
            </span>
            <div className="flex-1" />
            {selectedFiles.size > 0 && (
              <Button size="sm" variant="outline" onClick={handleBatchDownload}>
                <Download className="h-4 w-4 mr-1" /> Scarica
              </Button>
            )}
            <Button size="sm" variant="outline" className="text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={handleBatchDelete}>
              <Trash2 className="h-4 w-4 mr-1" /> Elimina
            </Button>
            <Button size="sm" variant="ghost" onClick={clearSelection}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        {uploading && (
          <div className="bg-blue-50 dark:bg-blue-900/30 border-b px-4 py-2 flex items-center gap-3">
            <RefreshCw className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400" />
            <span className="text-sm text-blue-700 dark:text-blue-300">Caricamento in corso...</span>
            <Progress value={uploadProgress} className="flex-1 h-2" />
            <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">{uploadProgress}%</span>
          </div>
        )}

        {sidebarSection === 'drive' ? (
          <div className="flex-1 overflow-y-auto p-4">
            {driveAccounts.length > 1 && (
              <div className="mb-4 flex items-center gap-3">
                <label className="text-sm font-medium text-muted-foreground">Account:</label>
                <select
                  value={selectedDriveAccount}
                  onChange={(e) => {
                    setSelectedDriveAccount(e.target.value);
                    setDriveFolderId('root');
                    setDriveBreadcrumbs([]);
                  }}
                  className="text-sm border rounded-lg px-3 py-1.5 bg-card"
                >
                  {driveAccounts.map(acc => (
                    <option key={acc.id} value={acc.id}>
                      {acc.account_label || acc.account_email || acc.id}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {driveAccounts.length === 0 && !driveLoading && (
              <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-border rounded-lg">
                <svg className="h-12 w-12 mb-4 opacity-30" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
                  <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
                  <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-20.4 35.3c-.8 1.4-1.2 2.95-1.2 4.5h27.5z" fill="#00ac47"/>
                  <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l11.752 23.8z" fill="#ea4335"/>
                  <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
                  <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/>
                  <path d="m73.4 26.5-10.2-17.7c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 23.8h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/>
                </svg>
                <h3 className="text-lg font-medium text-foreground mb-2">Nessun account Google collegato</h3>
                <p className="text-muted-foreground text-center mb-4">Collega un account Google per accedere a Google Drive.</p>
                <Button
                  onClick={async () => {
                    try {
                      const data = await apiClient.get<GoogleOauthStartData>(`/integrations/google/oauth/start?scopes=drive&new_account=true`);
                      if (data.success && data.data?.authUrl) window.location.href = data.data.authUrl;
                      else alert(data.error || 'Configura prima Client ID e Client Secret nelle Integrazioni');
                    } catch (e) { alert(getErrorMessage(e) || 'Errore durante la connessione Google'); }
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" /> Collega account Google
                </Button>
              </div>
            )}

            {driveAccounts.length > 0 && !driveLoading && selectedDriveAccount && (() => {
              const selAcc = driveAccounts.find(a => a.id === selectedDriveAccount);
              return selAcc && !selAcc.config?.enabled_scopes?.includes('drive');
            })() && (
              <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Autorizzazione Drive mancante</p>
                  <p className="text-xs text-amber-600 dark:text-amber-400">Questo account non ha il permesso Google Drive. Autorizza l'accesso per navigare i file.</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                  onClick={async () => {
                    const acc = driveAccounts.find(a => a.id === selectedDriveAccount);
                    if (!acc) return;
                    const currentScopes = acc.config?.enabled_scopes || [];
                    const newScopes = [...new Set([...currentScopes, 'drive'])];
                    try {
                      const data = await apiClient.get<GoogleOauthStartData>(`/integrations/google/oauth/start?scopes=${newScopes.join(',')}&account_id=${acc.id}`);
                      if (data.success && data.data?.authUrl) window.location.href = data.data.authUrl;
                    } catch (e) { alert(getErrorMessage(e) || 'Errore'); }
                  }}
                >
                  <RefreshCw className="h-3 w-3 mr-1" /> Autorizza Drive
                </Button>
              </div>
            )}

            {driveLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : driveFiles.length === 0 && driveAccounts.length > 0 ? (
              <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-border rounded-lg">
                <FolderOpen className="h-12 w-12 text-muted-foreground/60 mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">Cartella vuota</h3>
                <p className="text-muted-foreground">Questa cartella di Google Drive non contiene file.</p>
              </div>
            ) : (
              <div className={viewMode === 'grid' ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3' : 'bg-card rounded-lg border overflow-hidden'}>
                {viewMode === 'list' && driveFiles.length > 0 && (
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-muted-foreground">
                      <tr>
                        <th className="text-left px-4 py-2 font-medium">Nome</th>
                        <th className="text-left px-4 py-2 font-medium">Dimensione</th>
                        <th className="text-left px-4 py-2 font-medium">Ultima modifica</th>
                        <th className="text-right px-4 py-2 font-medium">Azioni</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {driveFiles.map((df) => (
                        <tr
                          key={df.id}
                          className="hover:bg-muted/50 cursor-pointer"
                          onClick={() => df.isFolder ? navigateDriveFolder(df.id, df.name) : undefined}
                        >
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              {getDriveFileIcon(df.mimeType, df.isFolder)}
                              <span className="font-medium truncate max-w-xs">{df.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-muted-foreground">{df.size ? formatBytes(parseInt(df.size)) : df.isFolder ? '-' : '-'}</td>
                          <td className="px-4 py-2 text-muted-foreground">{df.modifiedTime ? formatDate(df.modifiedTime) : '-'}</td>
                          <td className="px-4 py-2 text-right">
                            {!df.isFolder && (
                              <div className="flex items-center justify-end gap-1">
                                <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setImportFromDriveDialog({ open: true, driveFile: df }); }}>
                                  <Import className="h-4 w-4 mr-1" /> Importa
                                </Button>
                                {df.webViewLink && (
                                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); window.open(df.webViewLink, '_blank'); }}>
                                    <ExternalLink className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {viewMode === 'grid' && driveFiles.map((df) => (
                  <Card
                    key={df.id}
                    className="cursor-pointer hover:shadow-md transition-all group"
                    onClick={() => df.isFolder ? navigateDriveFolder(df.id, df.name) : undefined}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between mb-2">
                        <div className="p-2 rounded-lg bg-muted/50">
                          {getDriveFileIcon(df.mimeType, df.isFolder)}
                        </div>
                        {!df.isFolder && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="p-1 rounded hover:bg-muted opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                                <MoreVertical className="h-4 w-4 text-muted-foreground" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setImportFromDriveDialog({ open: true, driveFile: df }); }}>
                                <Import className="h-4 w-4 mr-2" /> Importa nel File Manager
                              </DropdownMenuItem>
                              {df.webViewLink && (
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); window.open(df.webViewLink, '_blank'); }}>
                                  <ExternalLink className="h-4 w-4 mr-2" /> Apri in Google Drive
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                      <p className="text-sm font-medium text-foreground truncate" title={df.name}>{df.name}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-muted-foreground">{df.size ? formatBytes(parseInt(df.size)) : df.isFolder ? 'Cartella' : '-'}</span>
                        <span className="text-xs text-muted-foreground">{df.modifiedTime ? formatDate(df.modifiedTime) : ''}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        ) : sidebarSection === 'groups' ? (
          <div className="flex-1 overflow-y-auto p-4">
            {!selectedGroup ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {groupsList.length === 0 ? (
                  <div className="col-span-full text-center py-12">
                    <Users className="h-10 w-10 text-muted-foreground/60 mx-auto mb-2" />
                    <p className="text-sm font-medium text-muted-foreground">Nessun gruppo</p>
                    <p className="text-xs text-muted-foreground mt-1">I file dei tuoi gruppi appariranno qui</p>
                  </div>
                ) : groupsList.map(g => (
                  <Card key={g.id} className="cursor-pointer hover:shadow-md hover:border-blue-200 transition-all" onClick={() => { setSelectedGroup(g); loadGroupFiles(g.id); }}>
                    <CardContent className="p-4 text-center">
                      <div className="h-12 w-12 rounded-xl bg-green-50 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-2">
                        <Users className="h-6 w-6 text-green-500" />
                      </div>
                      <p className="text-sm font-medium text-foreground truncate">{g.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">{g.member_count || g.members?.length || 0} membri</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-end mb-3">
                  <input
                    type="file"
                    id="group-file-upload"
                    className="hidden"
                    multiple
                    onChange={async (e) => {
                      const files = e.target.files;
                      if (!files || !selectedGroup) return;
                      for (const file of Array.from(files)) {
                        const fd = new FormData();
                        fd.append('file', file);
                        try {
                          await apiClient.post(`/messenger/workgroups/${selectedGroup.id}/files`, fd);
                        } catch (e) {
                          console.error('FileManager: group upload error:', e);
                        }
                      }
                      loadGroupFiles(selectedGroup.id);
                      e.target.value = '';
                    }}
                  />
                  <Button size="sm" className="gap-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg" onClick={() => document.getElementById('group-file-upload')?.click()}>
                    <Upload className="h-3.5 w-3.5" />Carica file
                  </Button>
                </div>
                {groupFiles.length === 0 ? (
                  <div className="text-center py-12">
                    <FolderOpen className="h-10 w-10 text-muted-foreground/60 mx-auto mb-2" />
                    <p className="text-sm font-medium text-muted-foreground">Nessun file in questo gruppo</p>
                    <p className="text-xs text-muted-foreground mt-1">Carica file o condividili dalla vista Documenti</p>
                  </div>
                ) : (
                  <div className={viewMode === 'grid' ? "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" : "space-y-1"}>
                    {groupFiles.map((f: GroupFile) => {
                      const isImage = f.mime_type?.startsWith('image/');
                      const aiEnabled = storageSettings?.ai_summary_enabled === true || quota?.ai_summary_enabled === true;
                      const fileActionsMenu = (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => e.stopPropagation()}>
                              <MoreVertical className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleGroupDownload(f)}>
                              <Download className="h-4 w-4 mr-2" />Download
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setGroupRenameDialog({ open: true, file: f }); setGroupNewName(f.file_name); }}>
                              <Edit2 className="h-4 w-4 mr-2" />Rinomina
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleGroupOpenAISummary(f)} disabled={!aiEnabled}>
                              <Sparkles className="h-4 w-4 mr-2 text-purple-500" />Riassunto AI
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleGroupDelete(f)} className="text-red-600 dark:text-red-400">
                              <Trash2 className="h-4 w-4 mr-2" />Elimina
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      );
                      return viewMode === 'grid' ? (
                        <Card key={f.id} className="hover:shadow-md transition-all">
                          <CardContent className="p-3">
                            <div className="flex items-start justify-between">
                              <div className="h-10 w-10 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center mb-2">
                                {isImage && f.cloud_path ? <img src={storageFileUrl(f.cloud_path)} alt="" className="h-10 w-10 rounded-lg object-cover" /> : <FileText className="h-5 w-5 text-blue-500" />}
                              </div>
                              {fileActionsMenu}
                            </div>
                            <p className="text-sm font-medium text-foreground truncate" title={f.file_name}>{f.file_name}</p>
                            <div className="flex items-center justify-between mt-1">
                              <span className="text-xs text-muted-foreground">{f.file_size ? formatBytes(Number(f.file_size)) : '-'}</span>
                              <span className="text-xs text-muted-foreground">{f.created_at ? formatDate(f.created_at) : ''}</span>
                            </div>
                            {f.source && (
                              <div className="mt-1.5">
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                  f.source === 'chat' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' :
                                  f.source === 'feed' ? 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400' :
                                  f.source === 'task' ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' :
                                  'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                                }`}>
                                  {f.source === 'chat' ? 'Chat' : f.source === 'feed' ? 'Feed' : f.source === 'task' ? 'Task' : 'Diretto'}
                                </span>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ) : (
                        <div key={f.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50 transition-colors">
                          <div className="h-8 w-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
                            {isImage && f.cloud_path ? <img src={storageFileUrl(f.cloud_path)} alt="" className="h-8 w-8 rounded-lg object-cover" /> : <FileText className="h-4 w-4 text-blue-500" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{f.file_name}</p>
                          </div>
                          {f.source && (
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${
                              f.source === 'chat' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' :
                              f.source === 'feed' ? 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400' :
                              f.source === 'task' ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' :
                              'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                            }`}>
                              {f.source === 'chat' ? 'Chat' : f.source === 'feed' ? 'Feed' : f.source === 'task' ? 'Task' : 'Diretto'}
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground shrink-0">{f.file_size ? formatBytes(Number(f.file_size)) : '-'}</span>
                          <span className="text-xs text-muted-foreground shrink-0">{f.created_at ? formatDate(f.created_at) : ''}</span>
                          {fileActionsMenu}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : sidebarSection === 'analytics' ? renderAnalytics() :
         sidebarSection === 'settings' ? renderSettings() : (
          <div
            className={`flex-1 overflow-y-auto p-4 transition-colors ${
              isDragOver && currentCategory ? 'bg-blue-50 dark:bg-blue-900/30 border-2 border-dashed border-blue-300 dark:border-blue-700' : ''
            }`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {isDragOver && currentCategory && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-blue-50/80 dark:bg-blue-900/40 pointer-events-none">
                <div className="text-center">
                  <Upload className="h-12 w-12 text-blue-500 mx-auto mb-2" />
                  <p className="text-lg font-medium text-blue-700 dark:text-blue-300">Rilascia per caricare</p>
                </div>
              </div>
            )}

            {loading || loadingFiles ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : !currentCategory && sidebarSection === 'categories' ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {visibleCategories.map((cat) => {
                  const Icon = cat.icon;
                  return (
                    <Card
                      key={cat.id}
                      className="cursor-pointer hover:shadow-md transition-all"
                      onClick={() => selectCategory(cat.id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`p-2 rounded-lg ${cat.bgColor}`}>
                            <Icon className={`h-6 w-6 ${cat.color}`} />
                          </div>
                          <h3 className="font-medium text-foreground">{cat.label}</h3>
                        </div>
                        {cat.adminOnly && (
                          <Badge variant="outline" className="text-[10px]">Solo Admin</Badge>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : displayFiles.length === 0 && folders.length === 0 && (sidebarSection !== 'shared' || sharedFolders.length === 0) ? (
              <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-border rounded-lg">
                {isTrashView ? (
                  <>
                    <Trash2 className="h-12 w-12 text-muted-foreground/60 mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">Cestino vuoto</h3>
                    <p className="text-muted-foreground">I file eliminati appariranno qui.</p>
                  </>
                ) : (
                  <>
                    <FolderOpen className="h-12 w-12 text-muted-foreground/60 mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      {searchTerm ? 'Nessun risultato' : sidebarSection === 'shared' ? 'Nessun file condiviso' : 'Cartella vuota'}
                    </h3>
                    <p className="text-muted-foreground text-center mb-4">
                      {searchTerm
                        ? 'Nessun file corrisponde alla ricerca.'
                        : sidebarSection === 'categories'
                          ? 'Trascina qui i file o clicca per caricare.'
                          : ''}
                    </p>
                    {!searchTerm && currentCategory && sidebarSection === 'categories' && (
                      <Button onClick={() => fileInputRef.current?.click()}>
                        <Upload className="h-4 w-4 mr-2" />
                        Carica file
                      </Button>
                    )}
                  </>
                )}
              </div>
            ) : (
              <>
                {sharedFolders.length > 0 && sidebarSection === 'shared' && (
                  <div className={`mb-4 ${viewMode === 'grid' ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3' : 'space-y-1'}`}>
                    {sharedFolders.map((folder) => (
                      <Card
                        key={folder.id}
                        className="cursor-pointer hover:shadow-md transition-all group"
                        onDoubleClick={() => { selectCategory('document'); navigateToFolder(folder.id, folder.name); }}
                        onClick={() => viewMode === 'list' && (() => { selectCategory('document'); navigateToFolder(folder.id, folder.name); })()}
                      >
                        <CardContent className={viewMode === 'grid' ? 'p-3' : 'p-2 flex items-center gap-3'}>
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <FolderOpen className="h-5 w-5 text-yellow-500 shrink-0" />
                            <span className="text-sm font-medium text-foreground truncate">{folder.name}</span>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <Badge variant="outline" className="text-[10px] text-indigo-600 dark:text-indigo-400">
                              <Users className="h-3 w-3 mr-1" />
                              {folder.permission === 'readwrite' ? 'Lettura/Scrittura' : 'Lettura'}
                            </Badge>
                          </div>
                        </CardContent>
                        {folder.shared_by_name && (
                          <div className="px-3 pb-2">
                            <span className="text-[11px] text-muted-foreground">Da: {folder.shared_by_name}</span>
                          </div>
                        )}
                      </Card>
                    ))}
                  </div>
                )}

                {folders.length > 0 && sidebarSection === 'categories' && (
                  <div className={`mb-4 ${viewMode === 'grid' ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3' : 'space-y-1'}`}>
                    {folders.map((folder) => (
                      <Card
                        key={folder.id}
                        className="cursor-pointer hover:shadow-md transition-all group"
                        onDoubleClick={() => navigateToFolder(folder.id, folder.name)}
                        onClick={() => viewMode === 'list' && navigateToFolder(folder.id, folder.name)}
                      >
                        <CardContent className={viewMode === 'grid' ? 'p-3' : 'p-2 flex items-center gap-3'}>
                          <div className="flex items-center justify-between w-full">
                            <div className="flex items-center gap-2 min-w-0">
                              {!folder.is_system && (
                                <input
                                  type="checkbox"
                                  checked={selectedFolders.has(folder.id)}
                                  onChange={(e) => { e.stopPropagation(); toggleFolderSelect(folder.id); }}
                                  onClick={(e) => e.stopPropagation()}
                                  className="h-4 w-4 rounded border-border shrink-0"
                                />
                              )}
                              <FolderOpen className="h-5 w-5 text-yellow-500 shrink-0" />
                              <span className="text-sm font-medium text-foreground truncate">{folder.name}</span>
                              {folder.is_system && (
                                <Badge variant="outline" className="text-[10px] shrink-0">Sistema</Badge>
                              )}
                            </div>
                            {!folder.is_system && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button className="p-1 rounded hover:bg-muted opacity-0 group-hover:opacity-100 transition-opacity shrink-0" onClick={(e) => e.stopPropagation()}>
                                    <MoreVertical className="h-4 w-4 text-muted-foreground" />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openShareDialog(undefined, folder); }}>
                                    <Share2 className="h-4 w-4 mr-2" /> Condividi
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder); }} className="text-red-600 dark:text-red-400">
                                    <Trash2 className="h-4 w-4 mr-2" /> Elimina
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                    {displayFiles.map((file) => (
                      <Card key={file.id} className={`hover:shadow-md transition-all group ${selectedFiles.has(file.id) ? 'ring-2 ring-blue-400 bg-blue-50/30 dark:bg-blue-900/20' : ''}`}>
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {!isTrashView && (
                                <input
                                  type="checkbox"
                                  checked={selectedFiles.has(file.id)}
                                  onChange={() => toggleFileSelect(file.id)}
                                  className="h-4 w-4 rounded border-border shrink-0"
                                />
                              )}
                              <div className="p-2 rounded-lg bg-muted/50">
                                {getFileIcon(file.mime_type)}
                              </div>
                            </div>
                            {renderFileActions(file, isTrashView)}
                          </div>
                          <p className="text-sm font-medium text-foreground truncate" title={file.file_name}>{file.file_name}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-muted-foreground">{formatBytes(file.file_size)}</span>
                            <span className="text-xs text-muted-foreground">{formatDate(file.created_at)}</span>
                          </div>
                          {!isTrashView && (
                            <div className="mt-1 flex items-center gap-1 flex-wrap">
                              {file.cloud_synced ? (
                                <Badge variant="outline" className="text-[10px] text-green-600 dark:text-green-400">
                                  <Cloud className="h-3 w-3 mr-1" /> Synced
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] text-yellow-600 dark:text-yellow-400">
                                  <CloudOff className="h-3 w-3 mr-1" /> Locale
                                </Badge>
                              )}
                              {file.ai_summary && (
                                <Badge variant="outline" className="text-[10px] text-purple-600 dark:text-purple-400 cursor-pointer" onClick={() => handleOpenAISummary(file)}>
                                  <Sparkles className="h-3 w-3 mr-1" /> AI
                                </Badge>
                              )}
                            </div>
                          )}
                          {isTrashView && (
                            <div className="mt-1">
                              <Badge variant="outline" className="text-[10px] text-red-500">
                                <Clock className="h-3 w-3 mr-1" /> {formatDate(file.updated_at)}
                              </Badge>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="bg-card rounded-lg border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 text-muted-foreground">
                        <tr>
                          {!isTrashView && <th className="px-2 py-2 w-8"></th>}
                          <th className="text-left px-4 py-2 font-medium">Nome</th>
                          <th className="text-left px-4 py-2 font-medium">Dimensione</th>
                          <th className="text-left px-4 py-2 font-medium">Categoria</th>
                          <th className="text-left px-4 py-2 font-medium">Data</th>
                          <th className="text-left px-4 py-2 font-medium">Stato</th>
                          <th className="text-right px-4 py-2 font-medium">Azioni</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {displayFiles.map((file) => (
                          <tr key={file.id} className={`hover:bg-muted/50 ${selectedFiles.has(file.id) ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}>
                            {!isTrashView && (
                              <td className="px-2 py-2">
                                <input
                                  type="checkbox"
                                  checked={selectedFiles.has(file.id)}
                                  onChange={() => toggleFileSelect(file.id)}
                                  className="h-4 w-4 rounded border-border"
                                />
                              </td>
                            )}
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                {getFileIcon(file.mime_type)}
                                <span className="font-medium truncate max-w-xs" title={file.file_name}>{file.file_name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-2 text-muted-foreground">{formatBytes(file.file_size)}</td>
                            <td className="px-4 py-2">
                              <Badge variant="secondary" className="text-xs">{getCategoryLabel(file.category)}</Badge>
                            </td>
                            <td className="px-4 py-2 text-muted-foreground">{formatDate(isTrashView ? file.updated_at : file.created_at)}</td>
                            <td className="px-4 py-2">
                              {isTrashView ? (
                                <Badge variant="outline" className="text-[10px] text-red-500">
                                  <Trash2 className="h-3 w-3 mr-1" /> Eliminato
                                </Badge>
                              ) : file.cloud_synced ? (
                                <Badge variant="outline" className="text-[10px] text-green-600 dark:text-green-400">
                                  <Cloud className="h-3 w-3 mr-1" /> Synced
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] text-yellow-600 dark:text-yellow-400">
                                  <CloudOff className="h-3 w-3 mr-1" /> Locale
                                </Badge>
                              )}
                            </td>
                            <td className="px-4 py-2 text-right">
                              {renderFileActions(file, isTrashView)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <Dialog open={newFolderDialog} onOpenChange={setNewFolderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuova cartella</DialogTitle>
            <DialogDescription>Inserisci il nome della nuova cartella.</DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Nome cartella"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewFolderDialog(false)}>Annulla</Button>
            <Button onClick={handleCreateFolder} disabled={!newFolderName.trim()}>Crea</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={renameDialog.open} onOpenChange={(open) => !open && setRenameDialog({ open: false, file: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rinomina file</DialogTitle>
            <DialogDescription>Inserisci il nuovo nome per il file.</DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Nuovo nome"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleRename()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialog({ open: false, file: null })}>Annulla</Button>
            <Button onClick={handleRename} disabled={!newName.trim()}>Rinomina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task #2834 — rinomina file di gruppo */}
      <Dialog open={groupRenameDialog.open} onOpenChange={(open) => !open && setGroupRenameDialog({ open: false, file: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rinomina file</DialogTitle>
            <DialogDescription>Inserisci il nuovo nome per il file.</DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Nuovo nome"
            value={groupNewName}
            onChange={(e) => setGroupNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleGroupRename()}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupRenameDialog({ open: false, file: null })}>Annulla</Button>
            <Button onClick={handleGroupRename} disabled={!groupNewName.trim()}>Rinomina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task #2834 — riassunto AI file di gruppo */}
      <Dialog open={groupAiSummaryDialog.open} onOpenChange={(open) => {
        if (!open && !groupAiSummaryDialog.loading) {
          setGroupAiSummaryDialog({ open: false, file: null, loading: false, summary: null, generatedAt: null });
        }
      }}>
        <DialogContent className="max-w-lg" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => { if (groupAiSummaryDialog.loading) e.preventDefault(); }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-500" />
              Riassunto AI
            </DialogTitle>
            <DialogDescription>
              {groupAiSummaryDialog.file?.file_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {groupAiSummaryDialog.loading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <RefreshCw className="h-8 w-8 animate-spin text-purple-500 mb-3" />
                <p className="text-sm text-muted-foreground">Generazione del riassunto in corso...</p>
                <p className="text-xs text-muted-foreground mt-1">Potrebbe richiedere qualche secondo</p>
              </div>
            ) : groupAiSummaryDialog.summary ? (
              <>
                {groupAiSummaryDialog.generatedAt && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span>Generato il {formatDate(groupAiSummaryDialog.generatedAt)}</span>
                  </div>
                )}
                <div className="bg-muted/50 rounded-lg p-4 max-h-80 overflow-y-auto">
                  <p className="text-sm text-foreground whitespace-pre-wrap">{groupAiSummaryDialog.summary}</p>
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">Nessun riassunto disponibile.</p>
            )}
          </div>
          <DialogFooter>
            {!groupAiSummaryDialog.loading && groupAiSummaryDialog.summary && (
              <Button variant="outline" onClick={handleGroupRegenerateAISummary} className="mr-auto">
                <RefreshCw className="h-4 w-4 mr-2" />
                Rigenera
              </Button>
            )}
            {!groupAiSummaryDialog.loading && (
              <Button variant="outline" onClick={() => setGroupAiSummaryDialog({ open: false, file: null, loading: false, summary: null, generatedAt: null })}>
                Chiudi
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={moveDialog.open} onOpenChange={(open) => !open && setMoveDialog({ open: false, file: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sposta file</DialogTitle>
            <DialogDescription>Seleziona la cartella di destinazione.</DialogDescription>
          </DialogHeader>
          <div className="max-h-60 overflow-y-auto space-y-1">
            {folderTree.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Nessuna cartella disponibile.</p>
            ) : (
              folderTree.map((node) => (
                <FolderTreeItem key={node.id} node={node} onSelect={(id) => handleMove(id)} depth={0} />
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveDialog({ open: false, file: null })}>Annulla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={shareDialog.open} onOpenChange={(open) => !open && setShareDialog({ open: false, file: null, folder: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{shareDialog.folder ? 'Condividi cartella' : 'Condividi file'}</DialogTitle>
            <DialogDescription>Seleziona utenti o gruppi con cui condividere "{shareDialog.folder?.name || shareDialog.file?.file_name}".</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {shareGroups.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Gruppi</p>
                <div className="max-h-28 overflow-y-auto border rounded-lg p-2 space-y-1">
                  {shareGroups.map((g) => (
                    <label key={g.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedShareGroups.includes(g.id)}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedShareGroups(prev => [...prev, g.id]);
                          else setSelectedShareGroups(prev => prev.filter(id => id !== g.id));
                        }}
                        className="rounded"
                      />
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-green-500" />
                        <div>
                          <p className="text-sm font-medium">{g.name}</p>
                          <p className="text-xs text-muted-foreground">{g.member_count || g.members?.length || 0} membri</p>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Utenti</p>
              <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                {shareUsers.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-2">Nessun utente disponibile.</p>
                ) : (
                  shareUsers.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedShareUsers.includes(u.id)}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedShareUsers(prev => [...prev, u.id]);
                          else setSelectedShareUsers(prev => prev.filter(id => id !== u.id));
                        }}
                        className="rounded"
                      />
                      <div>
                        <p className="text-sm font-medium">{u.name}</p>
                        <p className="text-xs text-muted-foreground">{u.email}</p>
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <label className="text-sm text-muted-foreground">Permesso:</label>
              <select
                value={sharePermission}
                onChange={(e) => setSharePermission(e.target.value as 'read' | 'readwrite')}
                className="text-sm border rounded px-2 py-1"
              >
                <option value="read">Sola lettura</option>
                <option value="readwrite">Lettura/Scrittura</option>
              </select>
            </div>
            <div className="flex items-center gap-4">
              <label className="text-sm text-muted-foreground">Scadenza:</label>
              <Input
                type="date"
                value={shareExpiry}
                onChange={(e) => setShareExpiry(e.target.value)}
                className="w-40 h-8 text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareDialog({ open: false, file: null, folder: null })}>Annulla</Button>
            <Button onClick={handleShare} disabled={selectedShareUsers.length === 0 && selectedShareGroups.length === 0}>Condividi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={deleteFolderConfirm.open} onOpenChange={(open) => !open && setDeleteFolderConfirm({ open: false, folder: null, notEmpty: false })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className={`h-5 w-5 ${deleteFolderConfirm.notEmpty ? 'text-red-500' : 'text-yellow-500'}`} />
              {deleteFolderConfirm.notEmpty ? 'Cartella non vuota' : 'Elimina cartella'}
            </DialogTitle>
            <DialogDescription>
              {deleteFolderConfirm.notEmpty
                ? `La cartella "${deleteFolderConfirm.folder?.name}" contiene file o sotto-cartelle. Non è possibile eliminarla finché non è vuota.`
                : `Sei sicuro di voler eliminare la cartella "${deleteFolderConfirm.folder?.name}"?`
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteFolderConfirm({ open: false, folder: null, notEmpty: false })}>
              {deleteFolderConfirm.notEmpty ? 'Chiudi' : 'Annulla'}
            </Button>
            {!deleteFolderConfirm.notEmpty && (
              <Button variant="destructive" onClick={confirmDeleteFolder}>
                Elimina
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={syncToDriveDialog.open} onOpenChange={(open) => !open && setSyncToDriveDialog({ open: false, file: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cloud className="h-5 w-5 text-blue-500" />
              Sincronizza su Google Drive
            </DialogTitle>
            <DialogDescription>
              Carica "{syncToDriveDialog.file?.file_name}" su Google Drive.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {driveAccounts.length > 1 && (
              <div>
                <label className="text-sm font-medium text-muted-foreground block mb-1">Account Google</label>
                <select
                  value={selectedDriveAccount}
                  onChange={(e) => { const newAcct = e.target.value; setSelectedDriveAccount(newAcct); setSyncDriveFolderId(''); loadDriveFolders(undefined, newAcct); }}
                  className="w-full text-sm border rounded-lg px-3 py-2 bg-card"
                >
                  {driveAccounts.map(acc => (
                    <option key={acc.id} value={acc.id}>
                      {acc.account_label || acc.account_email || acc.id}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-muted-foreground block mb-1">Cartella di destinazione</label>
              <select
                value={syncDriveFolderId}
                onChange={(e) => setSyncDriveFolderId(e.target.value)}
                className="w-full text-sm border rounded-lg px-3 py-2 bg-card"
              >
                <option value="">Root (Il mio Drive)</option>
                {syncDriveFolders.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSyncToDriveDialog({ open: false, file: null })}>Annulla</Button>
            <Button onClick={handleSyncToDrive} disabled={syncDriveLoading}>
              {syncDriveLoading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Cloud className="h-4 w-4 mr-2" />}
              Sincronizza
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={importFromDriveDialog.open} onOpenChange={(open) => !open && setImportFromDriveDialog({ open: false, driveFile: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Import className="h-5 w-5 text-green-500" />
              Importa da Google Drive
            </DialogTitle>
            <DialogDescription>
              Importa "{importFromDriveDialog.driveFile?.name}" nel File Manager.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground block mb-1">Categoria</label>
              <select
                value={importCategory}
                onChange={(e) => setImportCategory(e.target.value)}
                className="w-full text-sm border rounded-lg px-3 py-2 bg-card"
              >
                {visibleCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground block mb-1">Cartella di destinazione</label>
              <select
                value={importFolderId}
                onChange={(e) => setImportFolderId(e.target.value)}
                className="w-full text-sm border rounded-lg px-3 py-2 bg-card"
              >
                <option value="">Root (nessuna cartella)</option>
                {(() => {
                  const flattenTree = (nodes: typeof folderTree, depth = 0): { id: string; name: string; depth: number }[] => {
                    const result: { id: string; name: string; depth: number }[] = [];
                    for (const node of nodes) {
                      result.push({ id: node.id, name: node.name, depth });
                      if (node.children?.length) result.push(...flattenTree(node.children, depth + 1));
                    }
                    return result;
                  };
                  return flattenTree(folderTree).map(f => (
                    <option key={f.id} value={f.id}>{'  '.repeat(f.depth)}{f.name}</option>
                  ));
                })()}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setImportFromDriveDialog({ open: false, driveFile: null }); setImportFolderId(''); }}>Annulla</Button>
            <Button onClick={handleImportFromDrive} disabled={importLoading}>
              {importLoading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Import className="h-4 w-4 mr-2" />}
              Importa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={aiSummaryDialog.open} onOpenChange={(open) => {
        if (!open && !aiSummaryDialog.loading) {
          setAiSummaryDialog({ open: false, file: null, loading: false, summary: null, generatedAt: null });
        }
      }}>
        <DialogContent className="max-w-lg" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => { if (aiSummaryDialog.loading) e.preventDefault(); }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-500" />
              Riassunto AI
            </DialogTitle>
            <DialogDescription>
              {aiSummaryDialog.file?.file_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {aiSummaryDialog.loading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <RefreshCw className="h-8 w-8 animate-spin text-purple-500 mb-3" />
                <p className="text-sm text-muted-foreground">Generazione del riassunto in corso...</p>
                <p className="text-xs text-muted-foreground mt-1">Potrebbe richiedere qualche secondo</p>
              </div>
            ) : aiSummaryDialog.summary ? (
              <>
                {aiSummaryDialog.generatedAt && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span>Generato il {formatDate(aiSummaryDialog.generatedAt)}</span>
                  </div>
                )}
                <div className="bg-muted/50 rounded-lg p-4 max-h-80 overflow-y-auto">
                  <p className="text-sm text-foreground whitespace-pre-wrap">{aiSummaryDialog.summary}</p>
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">Nessun riassunto disponibile.</p>
            )}
          </div>
          <DialogFooter>
            {!aiSummaryDialog.loading && aiSummaryDialog.summary && (
              <Button variant="outline" onClick={handleRegenerateAISummary} className="mr-auto">
                <RefreshCw className="h-4 w-4 mr-2" />
                Rigenera
              </Button>
            )}
            {!aiSummaryDialog.loading && (
              <Button variant="outline" onClick={() => setAiSummaryDialog({ open: false, file: null, loading: false, summary: null, generatedAt: null })}>
                Chiudi
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task #1326 — conferma delete con cascade su company_wiki_sources */}
      <Dialog
        open={wikiDeleteDialog.open}
        onOpenChange={(open) => {
          if (!open && !wikiDeleteDialog.deleting) {
            setWikiDeleteDialog({ open: false, file: null, impact: { count: 0, sources: [] }, cascade: true, deleting: false });
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Eliminazione permanente</DialogTitle>
            <DialogDescription>
              Stai per eliminare definitivamente <strong>{wikiDeleteDialog.file?.file_name}</strong>. Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 p-3 text-amber-900 dark:text-amber-100">
              Questo file è usato come fonte in <strong>{wikiDeleteDialog.impact.count}</strong>
              {wikiDeleteDialog.impact.count === 1 ? ' fonte ' : ' fonti '}
              della Wiki Company.
            </div>
            {wikiDeleteDialog.impact.sources.length > 0 && (
              <ul className="list-disc pl-5 max-h-32 overflow-y-auto text-muted-foreground">
                {wikiDeleteDialog.impact.sources.slice(0, 5).map((s) => (
                  <li key={s.id}><span className="font-mono text-xs mr-1">[{s.kind}]</span>{s.title}</li>
                ))}
                {wikiDeleteDialog.impact.sources.length > 5 && (
                  <li>… e altre {wikiDeleteDialog.impact.sources.length - 5}</li>
                )}
              </ul>
            )}
            <label className="flex items-start gap-2 cursor-pointer">
              <Checkbox
                checked={wikiDeleteDialog.cascade}
                onCheckedChange={(checked) => setWikiDeleteDialog((prev) => ({ ...prev, cascade: checked === true }))}
                disabled={wikiDeleteDialog.deleting}
              />
              <span className="leading-tight">
                Rimuovi anche la fonte dall'indice Wiki Company
                <span className="block text-xs text-muted-foreground">
                  (cancella le righe in <code>company_wiki_sources</code>, rigenera l'INDICE.md e logga la cronologia)
                </span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setWikiDeleteDialog({ open: false, file: null, impact: { count: 0, sources: [] }, cascade: true, deleting: false })}
              disabled={wikiDeleteDialog.deleting}
            >
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmWikiDelete}
              disabled={wikiDeleteDialog.deleting}
            >
              {wikiDeleteDialog.deleting ? 'Eliminazione…' : 'Conferma eliminazione'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FolderTreeItem({ node, onSelect, depth }: { node: FolderTreeNode; onSelect: (id: string) => void; depth: number }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div>
      <button
        onClick={() => onSelect(node.id)}
        className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30 text-left text-sm"
        style={{ paddingLeft: `${8 + depth * 16}px` }}
      >
        {node.children.length > 0 && (
          <button onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }} className="p-0.5">
            <ChevronRight className={`h-3 w-3 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </button>
        )}
        <FolderOpen className="h-4 w-4 text-yellow-500" />
        <span>{node.name}</span>
      </button>
      {expanded && node.children.map((child) => (
        <FolderTreeItem key={child.id} node={child} onSelect={onSelect} depth={depth + 1} />
      ))}
    </div>
  );
}
